/**
 * HorizontalNav — mega-menu navigation bar.
 * Groups 19+ routes into 8 top-level items with dropdown panels.
 * File kept as Sidebar.tsx so existing imports in AppShell don't break;
 * the export is now `HorizontalNav`.
 */

import { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Truck,
  Package,
  GanttChartSquare,
  Users,
  Building2,
  Box,
  Contact,
  MapPin,
  // Map, // TEMP-HIDE #1444 — only used by the hidden Geofences nav item
  Fuel,
  FileText,
  Handshake,
  Receipt,
  Banknote,
  BarChart3,
  // Warehouse, // TEMP-HIDE #1444 — only used by the hidden Cross-Dock nav item
  // ArrowLeftRight, // TEMP-HIDE #1444 — only used by the hidden EDI nav item
  Settings,
  Menu,
  X,
  LifeBuoy,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  GaugeCircle,
  Brain,
  UserCheck,
  Mail,
  Wallet,
  CreditCard,
  Wrench,
  DollarSign,
  // WAYP-392: Expenses module nav icon
  ReceiptText,
  ShieldCheck,
  ToggleLeft,
  Tag,
  Calculator, // DAT Margin Calculator (the FreightBid AI nav item using it stays TEMP-HIDE #1444)
  Star,
  CalendarOff,
  CalendarDays,
  ClipboardList,
  Plug,
  GitBranch,
  ShoppingCart,
  ListTodo,
  Mailbox,
  MailSearch,
  Send,
  Flag,
  Boxes,
  SlidersHorizontal,
  // WAYP-927 — sidebar icon for Driver Routes (V1 /bids/ → V2 /driver-routes/).
  Route,
  // Umbrella #966 (slice f/i) — Recurring Loads nav icon
  Repeat,
  // #1087: Custom Charges / Credits — Finance menu V1→V2 foundation.
  FileSpreadsheet,
  // #1154: Deductions & Reimbursements page — Scale = D/R per #1140 toolbar.
  Scale,
  // #1288 — USPS Load History icon; unused while that nav entry is
  // commented out (#2302). Restore alongside the entry.
  // History,
  // #1319 — Settings palette search input icon.
  Search,
  // #1320 — Recent rail header icon.
  Clock,
  // Process EDI (#1755) — USPS Carrier > Types child. "File with magnifier"
  // reads as "review the inbound EDI report for exceptions".
  FileSearch,
  // #1344 — Settings icon swaps for three ambiguous items.
  // Braces → Custom Data Types (curly-braces visual matches "field definitions").
  // ClipboardCheck → Purchase Order Types (clipboard-with-check, not shopping cart).
  // HandCoins → Other Charge / Reimbursement Types (money exchange/return,
  // distinguishes it from Expense Types which uses ReceiptText).
  // (ClipboardCheck re-added in #1534 — Purchase Order Types is now in
  // the General column rather than the removed Equipment column.)
  Braces,
  ClipboardCheck,
  HandCoins,
  // #1504 — Load Categories icon swap (6th of 6 from the original spec, missed
  // by #1344). Stacked Layers glyph reads as "grouping / categorization",
  // distinguishes from the other Loads-column icons (ListTodo, GitBranch,
  // DollarSign, Fuel) and from the generic FolderOpen used elsewhere.
  Layers,
  // #1505 — Trucky Hook footer scaffold (non-functional placeholder).
  // Sparkles is the conventional "AI / magic" glyph and pairs with the
  // "Ask Trucky to deep-link any setting" label. Behaviour is reserved
  // for a future Trucky integration ticket (see WAYP-trucky-deeplink).
  Sparkles,
  // The Integration API Keys sidebar entry (and its `Key` icon) moved into the
  // Settings → General Settings page as a super-admin-only tab.
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useUIStore } from '../../../stores/ui-store';
import { useAuthStore } from '../../../stores/auth-store';
import { useDashboardStore } from '../../../stores/dashboard-store';
import { useEnterpriseFeatures, useFeature, useFeaturePreference } from '../../../hooks/use-enterprise-features';
import { useDriverRouteModuleName } from '../../../hooks/use-driver-route-module-name';
import { useCompanyInfo, useCompanyPreferences } from '../../../hooks/use-settings';
import { useFourkitesPartners } from '../../../hooks/use-edi';
import { usePermissionsStore, type PermissionModule } from '../../../stores/permissions-store';
import { useFleetLabels } from '../../../hooks/use-business-type';
import { useDatRoleAccess } from '../../../hooks/use-dat-loadboard';
import {
  resolveAllowedDashboardsForRole,
  type DashboardId,
} from '@tracxtms/shared';
import { useDashboardPermissions } from '../../../hooks/use-dashboard-permissions';
import { useSharedDashboards } from '../../../hooks/use-shared-dashboards';
// #1319 — fuzzy-match scorer for the Settings palette.
import { fuzzyScoreFields } from '../../../utils/fuzzy-match';
// #1320 — Recent settings prefs hook (server-persisted per-user list).
import {
  useSettingsRecents,
  useTrackSettingsRecent,
  PINNED_SLOT_COUNT,
  RECENT_PANEL_SLOTS,
} from '../../../hooks/use-settings-recents';

/**
 * Map nav child `value` keys to enterprise feature keys (WAYP-289 system).
 * When a feature is disabled for the org, the corresponding nav item is hidden.
 * Nav values without an entry here are always shown (subject to permissions).
 */
const NAV_VALUE_TO_FEATURE_KEY: Partial<Record<string, string>> = {
  edi: 'edi',
  // #1790 — Commodity Categories master is gated by the `commodity_category`
  // PREFERENCE inside the Dispatch (General Freight) feature (not a standalone
  // feature key), so it can't use this feature-key map — see the dedicated
  // preference gate in `hiddenNavKeys` below.
  usps: 'mod_usps_main',
  livestock: 'mod_livestock_main',
  // ATL-21 — all three visible children of the consolidated Dispatch
  // dropdown (Planner, Quotes, Dispatch Loads) gate on the `mod_dispatch_freight_main`
  // enterprise feature ("Dispatch (General Freight)"). When the feature
  // is OFF for the org both rows are removed from the nav tree, and the
  // existing "all-children-hidden → parent-hidden" sweep in `visibleGroups`
  // automatically removes the entire Dispatch dropdown. The Operations
  // dropdown is unaffected because `dispatch` / `quotes` no longer live
  // there. Quotes still has its second-layer `enableQuotes` company-
  // preference gate inside `hiddenNavKeys` below — both layers must pass
  // for the row to render.
  dispatch: 'mod_dispatch_freight_main',
  quotes:   'mod_dispatch_freight_main',
  // WAYP-814 follow-up — Intermodal / FTL Brokerage / Livestock dropdowns.
  // Each appears only when its `mod_*_main` enterprise feature is enabled.
  // The Quotes child + (when present) the primary module page share the
  // same gate so the entire dropdown collapses when the feature is OFF.
  'intermodal-quotes':    'mod_intermodal_main',
  'ftl-brokerage':        'mod_ftl_brokerage_main',
  'ftl-brokerage-quotes': 'mod_ftl_brokerage_main',
  'livestock-quotes':     'mod_livestock_main',
  // WAYP-505 — top-level USPS Carrier group + its children inherit the same
  // mod_usps_main gate. When the feature is OFF for the org, all three entries
  // are removed from the nav tree (and the group itself is hidden because all
  // its visible children have been filtered out).
  'usps-carrier': 'mod_usps_main',
  'usps-mail-class-types': 'mod_usps_main',
  'usps-mail-purpose-types': 'mod_usps_main',
  // #1216 USPS Legacy Cleanup (Andrew 2026-05-21): Contracts lives only
  // under the USPS Carrier dropdown — gating it on `mod_usps_main` lets
  // the entire dropdown hide when the module is OFF. The page itself
  // stays reachable by direct URL (no route-level FeatureGate); only
  // the menu placement is gated.
  'contracts': 'mod_usps_main',
  // WAYP-814 follow-up — USPS Carrier dropdown's Quotes entry. Distinct
  // nav value from the dispatch `quotes` row so the second-layer
  // `enableQuotes` Company Preference gate (added in `hiddenNavKeys`
  // below) only applies to the dispatch surface; the USPS Quote surface
  // is gated solely by the `mod_usps_main` enterprise feature, matching
  // the rest of the USPS Carrier dropdown.
  'usps-quotes': 'mod_usps_main',
  // USPS Tenders V2 — quotes-based tender queue (isTender=true, module='usps').
  // Same first-layer feature gate as every other USPS Carrier entry.
  'usps-tenders': 'mod_usps_main',
  // Process EDI (#1755) — ATL-000726: layer-1 is `mod_usps_main`, like every
  // other USPS Carrier entry. It used to be `edi`, which made this the ONE child
  // of the group the USPS feature gate could not filter: a General Freight org
  // with `mod_usps_main` OFF but the `edi` feature ON (plus
  // `usps_process_edi:view`) kept one visible child, and `visibleGroups` only
  // hides a group when ALL of its children are hidden — so the whole "USPS
  // Carrier" menu stayed on screen (reported by BX Freight / Basin Express).
  // The EDI requirement is preserved as a second layer in `hiddenNavKeys`, so
  // the entry still needs BOTH features, not either one.
  'usps-process-edi': 'mod_usps_main',
  // WAYP-764 (umbrella) — USPS Trips. Same first-layer gate as the rest of
  // the USPS Carrier group: hidden when `mod_usps_main` is OFF for the org.
  // Permission gate is the new `trips` scope (registered in slice 02 of the
  // umbrella) via NAV_VALUE_TO_PERMISSION below.
  'usps-trips': 'mod_usps_main',
  // #1141 (USPS Carrier Loads V2 umbrella) — USPS Loads. First-layer
  // feature gate: hidden when `mod_usps_main` is OFF for the org.
  // Permission gate is the new `usps_loads` scope (registered in slice
  // #1142) via NAV_VALUE_TO_PERMISSION below.
  'usps-loads': 'mod_usps_main',
  'usps-planner': 'mod_usps_main',
  // USPS 5500 (#1556) — same first-layer feature gate as the rest of the
  // USPS Carrier group. Second layer: `usps_5500:view` + the `usps500`
  // slip-reason preference being set (see `hiddenNavKeys`).
  'usps-5500': 'mod_usps_main',
  'usps-renewals': 'mod_usps_main',
  // Reconcile Slips (#1564) — same first-layer feature gate as the rest of the
  // USPS Carrier group. Layer-2 is `reconcile_slips:view` only (the `usps500`
  // slip-reason preference no longer gates this module — it shows ALL
  // USPS-variant load slips).
  'reconcile-slips': 'mod_usps_main',
  // CLEAR Rates (#1652) — same first-layer feature gate as the rest of the
  // USPS Carrier group. Layer-2 is `clear_rates:view` + the `usps500`
  // slip-reason preference being set (see `hiddenNavKeys`, same as USPS 5500).
  'clear-rates': 'mod_usps_main',
  // FourKites weekly compliance scores. First-layer gate is `mod_usps_main`;
  // layer-2 is `fourkites_compliance:view`. NOT gated on the `usps500`
  // preference (it is not tied to slip reasons), so it stays out of hiddenNavKeys.
  'compliance-scores': 'mod_usps_main',
  // Same feature gate for the Dashboard-menu shortcut to the carrier scorecard.
  'dashboard-compliance': 'mod_usps_main',
  // FourKites Tracking — relocated from Operations into the USPS Carrier >
  // Loads column. It needs the same first-layer `mod_usps_main` gate as its
  // new siblings: without it, an org with `mod_usps_main` OFF but a
  // FourKites-enabled EDI partner would keep the whole USPS Carrier group
  // visible for the sake of this one child (the #1216 Contracts bug).
  // Layer-2 stays `edi:view` (NAV_VALUE_TO_PERMISSION) plus the data-driven
  // "has ≥1 FourKites partner" gate in `hiddenNavKeys`.
  'fourkites-monitoring': 'mod_usps_main',
  // #1763 — USPS Voucher Import. Same first-layer gate as the rest of the USPS
  // Carrier group; layer-2 is `usps_vouchers:view` + the `usps500` slip-reason
  // preference (see `hiddenNavKeys`, same as CLEAR Rates).
  'vouchers': 'mod_usps_main',
  // #1770 — USPS Reconciliation. Same first-layer gate as the rest of the USPS
  // Carrier group; layer-2 is `usps_reconciliation:view` + the `usps500` pref.
  'usps-reconciliation': 'mod_usps_main',
  'usps-no-miles-reconciliation': 'mod_usps_main',
  // #1288 — USPS Load History (read-only Completed-loads grid). Same
  // two-layer gate as USPS Loads itself: layer-1 here on
  // `mod_usps_main`, layer-2 via NAV_VALUE_TO_PERMISSION below on the
  // shared `usps_loads:view` perm.
  'usps-load-history': 'mod_usps_main',
  // Umbrella #966 (umbrella) — USPS Recurring Loads. Same first-layer gate as
  // the rest of the USPS Carrier group: hidden when `mod_usps_main` is OFF
  // for the org. Permission gate is the new `recurring` scope (registered
  // in slice b/i — #968) via NAV_VALUE_TO_PERMISSION below. The sidebar
  // menu item itself lands in a later slice (f).
  'usps-recurring-loads': 'mod_usps_main',
  // (time-off-records is not a sidebar nav item in V2)
  // WAYP-504 — Settings modules gated by enterprise features. The nav entry
  // is hidden when the corresponding feature is disabled for the org.
  'time-off-types':              'driver_availability_calendar',
  'department-types': 'notes_report',
  // Custom Templates settings page is hidden unless the org's `custom_templates`
  // enterprise feature is on (seed.ts — category 'general', defaultEnabled: false).
  'custom-templates': 'custom_templates',
  'carrier-requirement-types': 'carrier_requirements',
  // WAYP-389: Purchase Orders list page is gated by the same enterprise
  // feature key as the existing Purchase Order Types module (WAYP-369).
  'purchase-orders': 'purchase_orders',
  // #670: Work Orders nav entry is hidden unless the org's `workorders`
  // enterprise feature is on (seed.ts:3088 — category 'equipment',
  // defaultEnabled: false). Matches V1 behavior where the module
  // appears only when the WorkOrders enterprise switch is enabled.
  'work-orders': 'workorders',
  // Commission Rules settings page is hidden unless the org's
  // `enable_commission_module` enterprise feature is on.
  'commission-rules': 'enable_commission_module',
  // WAYP-392: Expenses module gated by the existing `expenses` enterprise
  // feature flag (seed.ts:3303 — category 'finance', defaultEnabled: false).
  'expenses': 'expenses',
  // WAYP-681 (umbrella #677) — Frequency Type lives under the top-level USPS
  // Carrier nav group (sibling of Mail Class Types / Mail Purpose Types).
  // First-layer gate is mod_usps_main; the second-layer
  // `hide_contracts_and_trips` preference check lives in `hiddenNavKeys`
  // below so the configJson lookup runs once per render alongside the other
  // preference-driven gates. The route URL is `/usps/frequency-types` — the
  // legacy `/settings/frequency-types` URL still redirects (App.tsx) so
  // existing bookmarks keep working.
  'frequency-types': 'mod_usps_main',
  // WAYP-927 — Driver Routes (V1 /bids/ → V2 /driver-routes/ port).
  // Gated on the parent USPS Carrier module (`mod_usps_main`).
  // The standalone `mod_driver_routes` feature was retired in #1216
  // USPS Legacy Cleanup (2026-05-21).
  'driver-routes': 'mod_usps_main',
  // WAYP-945 — Dispatch Loads variant nav children. The aggregate `dispatch-loads`
  // chooser is gated on the *primary* freight variant; the per-variant chip
  // visibility sweep below ensures the chooser still renders when at least one
  // of the 4 mod_*_main features is on (the chooser dims unavailable variants).
  // Per-variant deep-link entries (currently surfaced by direct URL only) inherit
  // their respective mod_*_main keys so the future per-variant nav children
  // (added when the chooser branches into a nested submenu) work without code
  // changes.
  'dispatch-loads':               'mod_dispatch_freight_main',
  'dispatch-loads-freight':       'mod_dispatch_freight_main',
  'dispatch-loads-intermodal':    'mod_intermodal_main',
  'dispatch-loads-livestock':     'mod_livestock_main',
  'dispatch-loads-ftl-brokerage': 'mod_ftl_brokerage_main',
  // 2026-07-28 — Recurring Load nav entries removed (moved into the loads
  // grid's tab group, gated per-variant inside DispatchLoadsPage).
  // WAYP-945 (pivot 2026-05-11) — per-variant History / Calendar placeholders
  // inherit their parent variant's feature gate so the whole variant group
  // collapses when its `mod_*_main` is off.
  'intermodal-history':           'mod_intermodal_main',
  'livestock-history':            'mod_livestock_main',
  'ftl-brokerage-history':        'mod_ftl_brokerage_main',
  // WAYP-395: Customs Brokers — first-layer enterprise feature gate. The
  // second-layer `enable_custom_broker_info` preference check lives in
  // `hiddenNavKeys` below so the configJson lookup runs once per render
  // alongside the other preference-driven gates. The third gate
  // (companyType ∈ {broker, mixed}) is enforced server-side; the nav stays
  // visible client-side as long as the licence + preference are ON, and
  // the page itself shows a blank-slate when the org-type check fails.
  'customs-brokers': 'mod_ftl_brokerage_main',
  // WAYP-394 — Manual Fuel sidebar entry under the Fuel group. Visibility
  // is gated solely by the `manual_fuel` enterprise feature flag (no
  // permission entry exists; see decisions.md §3).
  'manual-fuel': 'manual_fuel',
  // WAYP-952 — V1-parity Rating sub-pages, all gated by `customer:rating`.
  // All three share the SAME feature key so the whole Rating group
  // collapses when the org has the feature disabled; the
  // `customer:rating.rate_type` preference (`hiddenNavKeys` below)
  // then narrows the visible set to exactly one of the three.
  'billing-rates': 'customer:rating',
  'lane-location-rates': 'customer:rating',
  'lane-city-rates': 'customer:rating',
  'fsc-rates': 'customer:rating',
  'accessorial-rates': 'customer:rating',
  // #1137 — Customer Statements & Collections. Hidden until the org enables
  // `enable_statements_collections` under Settings ▸ Company Preferences ▸
  // Finance. Server returns 403 on the API as a defence-in-depth.
  'customer-statements': 'enable_statements_collections',
  // QA-1 (issue #1403, 2026-05-30): Trading Partners hidden when the EDI
  // enterprise feature is OFF. The Trading Partners page is the EDI
  // integration's master-data backing store; surfacing it without EDI
  // licensed has no operational meaning.
  'trading-partners': 'edi',
  // QA-2 (#1403): Expense Types is the master-data table for the
  // Expenses feature; should share the same gate as Settings ▸ Expenses
  // already did (gate keyed at line 177 above).
  'expense-types': 'expenses',
  // QA-5 (#1403): Driver / Carrier Rating settings (the rating-questions
  // master-data table) hidden when Driver Carrier Performance is OFF.
  // Matches the `customer:rating` pattern further up for the rate-related
  // sub-pages: the master-data shouldn't surface unless the operational
  // feature is enabled.
  'driver-rating-questions': 'driver_carrier_performance',
  // QA-6 (#1403): per-variant Dispatch children that weren't already
  // gated. `dispatch` + `dispatch-loads` + `quotes` already gate on
  // `mod_dispatch_freight_main`; History / Calendar / Tender are new
  // additions on top so the entire General-Freight tree collapses
  // when the licence is OFF.
  'dispatch-loads-history':  'mod_dispatch_freight_main',
  'dispatch-loads-tender':   'mod_dispatch_freight_main',
  // DAT – ATL-001428 (B1): Find Loads (DAT).
  'dat-find-loads':          'dat_loadboard',
  'dat-margin-calculator':   'dat_loadboard',
  // #1635 — FarEye API Tenders. First-layer gate on the `api_tendering`
  // enterprise feature: the nav entry (and the route) hide when the feature
  // is OFF for the org. Configuring the FarEye integration is part of that
  // feature's setup (requiresSetup: true), so this is the operator-facing
  // "integration configured" signal — there is no separate org-scoped
  // connected-integrations endpoint a non-admin operator can read (the
  // `/admin/integrations/org/:id` API is admin-only). Second layer is the
  // `edi:view` permission via NAV_VALUE_TO_PERMISSION below.
  'api-tenders': 'api_tendering',
  // ATL-001415 — AI Email Intake review queue (its settings are in Integrations).
  'email-intake': 'ai_email_load_creation',
  // #1635 — API Tender History (past tenders). Same `api_tendering` gate as the
  // queue entry above; sibling of EDI Tender History.
  'api-tenders-history': 'api_tendering',
  // Andrew QA 2026-05-30 (#1403): Finance → Escrow Accounts nav entry
  // hides when the consolidated `escrow` enterprise feature is OFF.
  // (The PAGE / RBAC scope is still named `escrow_accounts`; only the
  // feature licence gate moved back to the V1 `escrow` key.)
  escrow_accounts: 'escrow',
  // QuickBooks Online settings entry hidden unless the org has the
  // `quickbooks_online` enterprise feature (Finance category — split from
  // the QBD key so Online/Desktop orgs each see only their own entry).
  'quickbooks-online': 'quickbooks_online',
  // QBD ticket — QuickBooks Desktop settings entry hidden unless the org has
  // the `quickbooks_desktop` enterprise feature (same key the server's QBD
  // push routes enforce).
  'quickbooks-desktop': 'quickbooks_desktop',
  // #1731 — Google Calendar settings entry hidden unless the org has the
  // `calendar_google` Calendar feature enabled.
  'google-calendar': 'calendar_google',
  // #1740 — Outlook Calendar settings entry, gated on `calendar_outlook`.
  'outlook-calendar': 'calendar_outlook',
  // #1747 — Fuel Card Import (EFS), gated on `efs_integration`.
  'fuel-cards-import': 'efs_integration',
};

const NAV_VALUE_TO_DASHBOARD_ID: Partial<Record<string, DashboardId>> = {
  'dashboard-main': 'main',
  'dashboard-equipment-utilization': 'equipment-utilization',
  'dashboard-driver-activity': 'driver-activity',
  'dashboard-ai-optimization': 'ai-optimization',
  'dashboard-sla': 'sla',
};

/**
 * Maps nav child `value` keys to permission modules.
 * If a value isn't listed here it is always shown.
 */
// WAYP-267: Maps nav child `value` keys to permission modules.
// If a value isn't listed here it is always shown.
// Expanded in WAYP-264 to include all V2 modules.
// ATL-000801 — exported so `Sidebar.test.tsx` can assert that gated nav
// entries actually carry their permission mapping. This map went months with
// the Purchase Orders line commented out and nothing caught it.
export const NAV_VALUE_TO_PERMISSION: Record<string, PermissionModule> = {
  'dashboard-main': 'loads',
  'dashboard-equipment-utilization': 'equipment',
  'dashboard-driver-activity': 'drivers',
  'dashboard-ai-optimization': 'ai',
  'dashboard-sla': 'reports',
  dispatch: 'dispatch',
  maps: 'maps',
  loads: 'loads',
  // WAYP-814: Quotes now uses its own permission module (was sharing `loads`).
  // Module-level visibility is also gated on the `enableQuotes` Company
  // Preference (see `hiddenNavKeys` below) — both gates must pass to render.
  quotes: 'quotes',
  // USPS-CONTRACTS-06 (2026-04-29): Contracts now uses its own permission
  // scope (registered in USPS-CONTRACTS-02) and is re-homed under the USPS
  // Carrier nav group. The legacy entry was previously gated on `loads:view`
  // while sitting under Operations.
  contracts: 'contracts',
  'cross-dock': 'cross_dock',
  livestock: 'loads',
  usps: 'usps',
  // WAYP-505 — usps:view required for the new top-level group and its
  // children. The group itself is gated via any-child-visibility, but it's
  // safer to also list the parent value so permission logic is explicit.
  'usps-carrier': 'usps',
  'usps-mail-class-types': 'usps',
  'usps-mail-purpose-types': 'usps',
  // WAYP-814 follow-up — USPS Carrier > Quotes shares the same `quotes`
  // permission module as the dispatch surface (one permission, two
  // surfaces). If `quotes:view` is revoked, both surfaces are hidden.
  'usps-quotes': 'quotes',
  // WAYP-814 follow-up — Intermodal / FTL Brokerage / Livestock dropdown
  // entries. Quote rows share the `quotes` permission across surfaces;
  // the FTL Brokerage primary page (Freight Load Board) reuses the
  // generic `loads` permission to match the existing `/freight` route
  // gating in App.tsx.
  'intermodal-quotes':    'quotes',
  'ftl-brokerage':        'loads',
  'ftl-brokerage-quotes': 'quotes',
  'livestock-quotes':     'quotes',
  // WAYP-764 (umbrella) — USPS Trips uses its own granular `trips` permission
  // module (registered in slice 02). Six actions:
  // view | create | edit | delete | export | restore.
  'usps-trips': 'trips',
  // #1141 (USPS Carrier Loads V2 umbrella) — USPS Loads uses its own
  // granular `usps_loads` permission module (registered in slice
  // #1142). Six actions: view | create | edit | delete | export |
  // restore.
  'usps-loads': 'usps_loads',
  'usps-planner': 'usps_loads',
  // USPS 5500 (#1556) — dedicated `usps_5500` permission scope (view/edit/
  // delete/export).
  'usps-5500': 'usps_5500',
  'usps-renewals': 'usps_renewals',
  // Reconcile Slips (#1564) — dedicated `reconcile_slips` scope (view/edit/
  // delete/export/approve).
  'reconcile-slips': 'reconcile_slips',
  // CLEAR Rates (#1652) — dedicated `clear_rates` scope (view/import/export).
  'clear-rates': 'clear_rates',
  // FourKites weekly compliance scores — customer scope (`view`/`export`). The
  // super_admin upload/cross-org surface lives on the same page behind its own
  // `fourkites_compliance_admin` scope + a route-layer super_admin gate.
  'compliance-scores': 'fourkites_compliance',
  // Dashboard-menu shortcut to the carrier scorecard — same customer scope.
  'dashboard-compliance': 'fourkites_compliance',
  // #1763 — USPS Voucher Import uses its own `usps_vouchers` scope (view/import/export).
  'vouchers': 'usps_vouchers',
  // #1770 — USPS Reconciliation uses its own `usps_reconciliation` scope.
  'usps-reconciliation': 'usps_reconciliation',
  'usps-no-miles-reconciliation': 'usps_reconciliation',
  // #1288 — USPS Load History reuses the `usps_loads` permission
  // scope. Read-only, so only `view` is exercised.
  'usps-load-history': 'usps_loads',
  // USPS Tenders V2 — dedicated `usps_tenders` permission scope (view/export).
  'usps-tenders': 'usps_tenders',
  // Process EDI (#1755) — dedicated `usps_process_edi` scope (view/process/export).
  'usps-process-edi': 'usps_process_edi',
  // WAYP-927 — Driver Routes uses its own `driver_routes` permission scope
  // (six actions: view | create | edit | delete | export | restore).
  'driver-routes': 'driver_routes',
  // Umbrella #966 — USPS Recurring Loads uses its own granular `recurring`
  // permission module (registered in slice b/i — #968). Six actions:
  // view | create | edit | delete | export | restore.
  'usps-recurring-loads': 'recurring',
  edi: 'edi',
  drivers: 'drivers',
  // (time-off-records is not a sidebar nav item in V2)
  carriers: 'carriers',
  // WAYP-611: Supplies — top-level nav entry (sibling to Carriers, vendor master).
  supplies: 'supplies',
  customers: 'customers',    // WAYP-264: customers now has own module
  // WAYP-952 — V1-parity Rating sub-pages, each with its own permission scope.
  'billing-rates': 'billing_rates',
  // ATL-001214 (findings #13/#14) — were 'loads', so any dispatcher/manager
  // (anyone with loads:view) saw these nav entries even though the server's
  // GET /rates/lanes now requires billing_rates:view; left as-is they'd be
  // visible-but-broken (403 on load) for the same population the server
  // fix locks out.
  'lane-location-rates': 'billing_rates',
  'lane-city-rates': 'billing_rates',
  'fsc-rates': 'fsc_rates',
  'accessorial-rates': 'accessorial_rates',
  equipment: 'equipment',
  // WAYP-561.5 — Locations moved from Fleet (legacy top-level nav) into
  // Settings > System Settings and now uses its own `locations` permission
  // module (formerly borrowed `equipment`).
  locations: 'locations',
  geofences: 'maps',
  // #1077: permission module renamed `invoicing` → `invoices`. The sidebar
  // nav key stays as the URL segment `invoices`; the value is the perm key.
  invoices: 'invoices',
  settlements: 'settlements',
  // #1154 — Deductions page shares the Settlements permission module
  // (V1 parity: V1's `app('Deductions')` controller also gates on the
  // Settlements module access).
  deductions: 'settlements',
  // #1077: permission module renamed `invoicing` → `invoices`.
  'received-payments': 'invoices',
  // #1137 — Customer Statements & Collections page reuses the `invoices`
  // permission module (V1 grouped statements under the same accounting
  // role as invoices; V2 keeps parity).
  'customer-statements': 'invoices',
  // #920 — Escrow Accounts V2 V1-parity rebuild — own perm module (6 actions).
  escrow_accounts: 'escrow_accounts',
  // #898 — Loans gets its own permission module (V1 parity, 6 actions).
  'driver-loans': 'loans',
  'fuel-cards-fin': 'fuel',
  'fuel-cards-import': 'fuel',
  // #670: Work Orders — promoted from the legacy `invoices` (formerly `invoicing`) placeholder
  // permission to its own `work_orders` module (six actions registered in
  // permissions-store.ts). Existing nav entry at line ~345 remains unchanged.
  'work-orders': 'work_orders',
  // WAYP-389: Purchase Orders list page — gated by `purchase_orders:view`.
  // ATL-000801 — this mapping was temporarily commented out (TODO WAYP-385)
  // so the nav item stayed visible for manual testing before the
  // user-permissions UI existed. That UI has shipped, and the route itself is
  // guarded again in App.tsx; without this line the entry would still show for
  // every authenticated user and bounce them to /dashboard on click.
  'purchase-orders': 'purchase_orders',
  // WAYP-392: Expenses module — see existing `expenses` entry below (already present).
  'report-catalog': 'reports',
  fuel: 'fuel',
  reports: 'reports',
  support: 'support',
  roadmap: 'support',
  settings: 'settings',
  'company-info': 'settings',
  // WAYP-803: Settings → Company Preferences (V1 parity port)
  'company-preferences': 'settings',
  'deduction-types': 'deduction_types',
  'time-off-types':  'time_off_types',
  'equipment-types': 'equipment_types',
  'driver-expiry-types': 'driver_expiry_types',
  'expense-types':   'expense_types',
  'file-checklist':  'file_checklist',
  'department-types': 'department_types',
  'custom-templates': 'custom_templates',
  'carrier-requirement-types': 'carrier_requirement_types',
  // WAYP-837 — Location Types nav entry inherits the `locations` permission
  // module (LocationType has no dedicated permission module — it's a child
  // concept of Locations, mirrored from the server route gate).
  'location-types': 'locations',
  taxes: 'taxes',
  'trading-partners': 'trading_partners',
  'fuel-rates': 'fuel_rates',
  'equipment-expiry-types': 'equipment_expiry_types',
  terminals: 'terminals',
  'routing-row-types': 'routing_row_types',
  'load-categories': 'load_categories',
  'commodity-categories': 'commodity_categories',
  // WAYP-395: Customs Brokers permission module (granular RBAC, 7 actions).
  'customs-brokers': 'customs_brokers',
  'load-statuses': 'load_statuses',
  // WAYP-45 (Round 6, decision.md #40-#43) — standalone Custom Dispatch Tabs page.
  'custom-dispatch-tabs': 'custom_dispatch_tabs',
  'custom-data-types': 'custom_data_types',
  // WAYP-681 (umbrella #677) — Frequency Types module in Settings > USPS.
  'frequency-types': 'frequency_types',
  'rate-types': 'rate_service_types',
  // Weight-Based Zone Rating — Zones master (permission module `zones`).
  zones: 'zones',
  // WAYP-502: Other Charge / Reimbursement Types (nav entry added in WAYP-502.5)
  'other-charge-types': 'other_charge_types',
  // Commission module — category-based commission rules management.
  'commission-rules': 'settings',
  // Cross-load Other Charges & Reimbursements list (Finance menu).
  // Permission is the load family because rows are part of a load.
  'other-charges-reimbursements': 'loads',
  'slips': 'slips',
  documents: 'documents',
  maintenance: 'maintenance',
  notifications: 'notifications',
  gps: 'gps',
  payments: 'payments',
  expenses: 'expenses',
  brokerage: 'brokerage',
  'audit-logs': 'reports',
  // WAYP-945 — Dispatch Loads nav children all consult the `loads` permission
  // module (per Brief §5).
  'dispatch-loads':               'loads',
  'dispatch-loads-freight':       'loads',
  'dispatch-loads-intermodal':    'loads',
  'dispatch-loads-livestock':     'loads',
  'dispatch-loads-ftl-brokerage': 'loads',
  // ATL-000652 — the Tenders surface is quotes-based (a dispatch tender is a
  // Quote with isTender=true), so its nav gate consults the `quotes`
  // permission, matching the page guard + the /dispatch-tenders server routes
  // (`quotes:view`). NOT tied to the `enableQuotes` company preference — that
  // pref hides only the hand-authored Quotes workflow entry; ingested EDI
  // tenders surface regardless.
  'dispatch-loads-tender':        'quotes',
  'dat-find-loads':               'loads',
  'dat-margin-calculator':        'loads',
  'dispatch-loads-history':       'loads',
  // #1635 — FarEye API Tenders uses the `edi` permission scope (view), matching
  // the server router guard (`requirePermission('edi', 'view')`).
  'api-tenders':                  'edi',
  'email-intake':                 'email_intake',
  'api-tenders-history':          'edi',
  // FourKites Tracking dashboard — `edi` scope, matching the server route guard.
  'fourkites-monitoring':         'edi',
  // WAYP-945 (pivot 2026-05-11) — per-variant History / Calendar placeholders
  // all consult the `loads` permission module (Brief §5 — single `loads`
  // permission gates all 4 variants).
  'intermodal-history':           'loads',
  'livestock-history':            'loads',
  'ftl-brokerage-history':        'loads',
};

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface NavChild {
  label: string;
  value: string;          // used for hiddenNavKeys filtering
  href: string;
  icon: LucideIcon;
  description?: string;
  /** When true, renders as a non-link section group label (no href/icon used). */
  sectionHeader?: boolean;
  /** When true, the child is only rendered for users with role === 'super_admin'. */
  superAdminOnly?: boolean;
  /**
   * When true, the parent group only treats this child as "active" on an EXACT
   * path match (no `startsWith` prefix extension). Needed when a legacy entry's
   * href is a prefix of routes owned by a different, newer nav group — without
   * this flag both groups would highlight simultaneously. Default: false.
   */
  exact?: boolean;
}

interface NavColumnGroup {
  /**
   * Stable identity slug (camelCase of the English header). `header` is now a
   * translated display string, so structural lookups (tests, column matching)
   * must key off `key`, never `header`.
   */
  key: string;
  header: string;
  children: NavChild[];
  /**
   * Subtle background tint applied to every child item's icon swatch in the
   * mega-panel. Adds a second visual signal beyond column position so the
   * eye can lock onto a section (General / Equipment / Drivers / Loads /
   * System Settings) at a glance. Only used by the inactive state; active
   * items still adopt `--vz-primary-bg-subtle` so the selection cue wins.
   * Light enough (~0.05 luminance shift) to read as "tint" rather than
   * "color block" against the panel surface. Optional — columns that omit
   * it fall back to `--vz-body-bg`.
   */
  iconTint?: string;
}

interface NavGroup {
  /**
   * Stable identity slug (camelCase of the English label). `label` is now a
   * translated display string, so identity checks (`=== 'Dashboard'`, etc.)
   * must key off `key`, never `label`.
   */
  key: string;
  label: string;
  icon: LucideIcon;
  href?: string;          // direct link (no dropdown)
  value?: string;         // hiddenNavKeys key for direct-link items
  children?: NavChild[];
  /** Render dropdown as a multi-column mega-panel. */
  columnGroups?: NavColumnGroup[];
  /**
   * When true (and the group has a direct `href`), the group is only "active"
   * on an EXACT path match — no `startsWith` prefix extension. Mirrors the
   * same flag on `NavChild`. Default: false.
   */
  exact?: boolean;
  /**
   * #1319 — When true, the dropdown renders as a searchable palette. A
   * search input is mounted at the top of the panel and the visible
   * children are fuzzy-filtered live. The global Cmd+K shortcut opens
   * the first palette-flagged group in the nav.
   */
  palette?: boolean;
  /**
   * #1573 — Opt-in: a direct-link group navigates to `href` when the
   * cursor lingers over the trigger for ~250ms (hover-intent). Click
   * still works for keyboard activation and deliberate tap on touch.
   * Default: false. Only meaningful on groups with `href` and no
   * children/columnGroups.
   */
  navigateOnHover?: boolean;
}

/* ------------------------------------------------------------------ */
/*  Settings column tints                                              */
/* ------------------------------------------------------------------ */

/**
 * #1322 / #1345 — Single source of truth for the five Settings-palette
 * column tints. Each entry powers two visual surfaces:
 *   1. The 32×32 icon swatch on inactive children (`NavChildLink`).
 *   2. The 2px accent bar painted under the column header (#1345).
 * Both surfaces consume this map directly so the hex literals never get
 * duplicated. Keys match the `header` string on each `NavColumnGroup`.
 *
 * Tint chips were picked by design to be ~0.05 luminance shifts off the
 * panel surface — readable as "tint" rather than "color block" — so a
 * thin bar reads as an accent stripe (not a banner).
 *
 * #1347 — Values are now `var(--vz-settings-tint-*)` CSS variables. The
 * light-mode hex literals live in `globals.css` (`:root { … }`) and the
 * dark-mode rgba()-at-15%-alpha variants live in the same file's `.dark
 * { … }` block. Sidebar.tsx no longer owns the hex; theme flips
 * propagate automatically without JS theme detection.
 */
const SETTINGS_COLUMN_TINTS: Record<string, string> = {
  General:           'var(--vz-settings-tint-general)',
  Equipment:         'var(--vz-settings-tint-equipment)',
  Drivers:           'var(--vz-settings-tint-drivers)',
  Loads:             'var(--vz-settings-tint-loads)',
  'System Settings': 'var(--vz-settings-tint-system)',
};

/* ------------------------------------------------------------------ */
/*  Navigation structure                                               */
/* ------------------------------------------------------------------ */

/**
 * ATL-001214 finding #4 — an org that flips Rating on without yet picking
 * a `rate_type` option must see NONE of the three rate pages (V1 parity:
 * the feature is inert until an explicit choice is made). Extracted so the
 * unset-mode case (previously defaulted to 'Rate Types', silently showing
 * billing-rates) is unit-testable without rendering the whole Sidebar.
 */
export function ratingHiddenNavKeys(ratingPrefs: Record<string, unknown>): Set<string> {
  const hidden = new Set<string>();
  const rateTypeMode = (ratingPrefs.rate_type as string) || null;
  if (rateTypeMode !== 'Rate Types') hidden.add('billing-rates');
  if (rateTypeMode !== 'Lanes/Locations') hidden.add('lane-location-rates');
  if (rateTypeMode !== 'Lanes/Cities') hidden.add('lane-city-rates');
  if (!ratingPrefs.enable_fsc_per_customer) hidden.add('fsc-rates');
  if (!ratingPrefs.enable_accessorial_rates) hidden.add('accessorial-rates');
  return hidden;
}

// #1509 — exported (was `const`) so the column-layout reconcile test can
// assert palette card placement without re-rendering the whole nav. The
// runtime tree is unchanged; this is a static layout contract.
//
// i18n — nav labels/descriptions/column headers are translated at build time
// via `t()`, so this is now a builder function (was a module-scope const).
// `key` on each group / column carries the stable English-slug identity that
// callers and tests compare against — never compare against `label`/`header`.
/**
 * Super-admin-only nav items stay hidden from everyone else — except
 * Integrations for the people who configure Email Intake (ATL-001415: its
 * settings live there; the page then shows them only that card).
 */
export function passesSuperAdminGate(
  child: { value: string; superAdminOnly?: boolean },
  isSuperAdmin: boolean,
  configuresEmailIntake: boolean,
): boolean {
  if (!child.superAdminOnly || isSuperAdmin) return true;
  return child.value === 'admin-integrations' && configuresEmailIntake;
}

export function buildNavGroups(t: TFunction): NavGroup[] {
  return [
  {
    key: 'dashboard',
    label: t('components.sidebar.groups.dashboard.label'),
    icon: LayoutDashboard,
    children: [
      { label: t('components.sidebar.items.dashboard-main.label'), value: 'dashboard-main', href: '/dashboard', icon: LayoutDashboard, description: t('components.sidebar.items.dashboard-main.desc') },
      { label: t('components.sidebar.items.dashboard-equipment-utilization.label'), value: 'dashboard-equipment-utilization', href: '/dashboard/equipment-utilization', icon: GaugeCircle, description: t('components.sidebar.items.dashboard-equipment-utilization.desc') },
      { label: t('components.sidebar.items.dashboard-driver-activity.label'), value: 'dashboard-driver-activity', href: '/dashboard/driver-activity', icon: UserCheck, description: t('components.sidebar.items.dashboard-driver-activity.desc') },
      { label: t('components.sidebar.items.dashboard-ai-optimization.label'), value: 'dashboard-ai-optimization', href: '/dashboard/ai-optimization', icon: Brain, description: t('components.sidebar.items.dashboard-ai-optimization.desc') },
      { label: t('components.sidebar.items.dashboard-sla.label'), value: 'dashboard-sla', href: '/dashboard/sla', icon: GaugeCircle, description: t('components.sidebar.items.dashboard-sla.desc') },
      // FourKites compliance, carrier-facing view — a Dashboard-menu shortcut to
      // the same scorecard a carrier sees. Gated on `mod_usps_main` +
      // `fourkites_compliance:view` (NAV_VALUE_TO_FEATURE_KEY / _PERMISSION below).
      { label: t('components.sidebar.items.dashboard-compliance.label'), value: 'dashboard-compliance', href: '/dashboard/compliance-scores', icon: GaugeCircle, description: t('components.sidebar.items.dashboard-compliance.desc') },
    ],
  },
  {
    key: 'operations',
    label: t('components.sidebar.groups.operations.label'),
    icon: Package,
    children: [
      // #1157 — Loads, Trips, and Templates removed from the Operations
      // menu. The routes (`/loads`, `/trips`, `/loads/templates`) remain
      // registered in App.tsx for direct-URL access; only the menu
      // entries are hidden. Dispatch surfaces (top-level Dispatch group)
      // own the load/trip workflow now.
      // WAYP-814 follow-up — Quotes moved out of Operations and now lives
      // under the Dispatch dropdown group (defined above). The Quote
      // permission/feature gates are unchanged — they're keyed off the
      // nav `value`, which is preserved at the new location.
      // USPS-CONTRACTS-06 (2026-04-29): Contracts moved out of Operations
      // into the USPS Carrier nav group below. Old `/contracts` route is
      // removed entirely (the generic Contract module is retired in this
      // slice); the USPS-canonical `/usps/contracts` is the only
      // remaining Contracts entry.
      { label: t('components.sidebar.items.customers.label'), value: 'customers', href: '/customers', icon: Contact, description: t('components.sidebar.items.customers.desc') },
      { label: t('components.sidebar.items.locations.label'), value: 'locations', href: '/locations', icon: MapPin, description: t('components.sidebar.items.locations.desc') },
      // FourKites Tracking moved out of Operations into the USPS Carrier >
      // Loads column (sibling of Tenders / Process EDI, which are the other
      // USPS EDI surfaces). The route stays `/operations/fourkites-monitoring`
      // — same precedent as Slips, which lives under USPS Carrier while
      // keeping its `/settings/slips` URL, so existing bookmarks keep working.
      // TEMP-HIDE #1444: Cross-Dock + EDI hidden from Operations nav (routes
      // /cross-dock + /edi + permissions intact). Re-enable by uncommenting.
      // { label: 'Cross-Dock', value: 'cross-dock', href: '/cross-dock', icon: Warehouse, description: 'Cross-dock operations' },
      // { label: 'EDI', value: 'edi', href: '/edi', icon: ArrowLeftRight, description: 'Electronic data interchange' },
      // WAYP-814 follow-up — Livestock promoted from Operations into its
      // own top-level dropdown (sibling of USPS Carrier / Dispatch),
      // gated on the `mod_livestock_main` enterprise feature. The entry
      // moved alongside the new Livestock > Quotes child. See the new
      // top-level group below.
      // #1216 USPS Legacy Cleanup (Andrew 2026-05-21): legacy
      // Operations > USPS child removed; its Vouchers + Invoices tabs
      // were the last surfaces still living there and they've been
      // retired now that USPS Carrier V2 (PR #1211) owns the load
      // lifecycle. All other USPS surfaces live under the top-level
      // "USPS Carrier" group below.
    ],
  },
  {
    key: 'fleet',
    label: t('components.sidebar.groups.fleet.label'),
    icon: Truck,
    children: [
      {
        // #1639 — Maps V2 (live fleet map). Moved here from Dispatch (product
        // decision) to sit with the rest of Fleet. Gated by `maps:view`
        // (NAV_VALUE_TO_PERMISSION already maps `maps` -> `maps`).
        label: t('components.sidebar.items.maps.label'),
        value: 'maps',
        href: '/maps',
        icon: MapPin,
        description: t('components.sidebar.items.maps.desc'),
      },
      {
        label: t('components.sidebar.items.drivers.label'),
        value: 'drivers',
        href: '/drivers',
        icon: Users,
        description: t('components.sidebar.items.drivers.desc'),
      },
      // WAYP-1179/1215 — Driver Availability Calendar and Time Off Records are
      // accessed via the calendar icon on each driver row in the grid and via
      // the "Time Off Records" tab in the Drivers & Carriers page header.
      // They are NOT top-level Fleet nav entries (V1 parity decision).
      { label: t('components.sidebar.items.equipment.label'), value: 'equipment', href: '/equipment', icon: Box, description: t('components.sidebar.items.equipment.desc') },
      // WAYP-844: Suppliers nested under Fleet (was top-level per WAYP-611;
      // revised 2026-05-04 product decision to consolidate operational vendor
      // management under Fleet alongside Drivers, Carriers, Equipment, and
      // Locations). Permission gated on `supplies:view` via
      // NAV_VALUE_TO_PERMISSION above; route URL `/supplies` unchanged.
      { label: t('components.sidebar.items.supplies.label'), value: 'supplies', href: '/supplies', icon: Boxes, description: t('components.sidebar.items.supplies.desc') },
      // TEMP-HIDE #1444: Geofences hidden from Fleet nav (route /maps/geofences
      // + permissions intact). Re-enable by uncommenting this entry.
      // { label: 'Geofences', value: 'geofences', href: '/maps/geofences', icon: Map, description: 'Geofence boundaries' },
      // WAYP-395: Customs Brokers — master-data screen for broker organizations
      // managing cross-border shipments. Hidden until both the
      // `mod_customs_broker` enterprise feature is licensed AND the
      // Brokerage tab toggle (`enable_custom_broker_info`) is ON.
      // The third gate (`companyType ∈ {broker, mixed}`) is enforced
      // server-side, so non-eligible orgs that somehow reach the entry
      // see a blank-slate.
      { label: t('components.sidebar.items.customs-brokers.label'), value: 'customs-brokers', href: '/freight/customs-brokers', icon: Handshake, description: t('components.sidebar.items.customs-brokers.desc') },
    ],
  },
  // TEMP-HIDE #1444: FreightBid AI hidden from global nav (route /freightbid +
  // permissions intact). Re-enable by uncommenting this entry.
  // {
  //   label: 'FreightBid AI',
  //   icon: Calculator,
  //   href: '/freightbid',
  //   value: 'freightbid',
  // },
  {
    key: 'fuel',
    label: t('components.sidebar.groups.fuel.label'),
    icon: Fuel,
    children: [
      {
        label: t('components.sidebar.items.fuel.label'),
        value: 'fuel',
        href: '/fuel',
        icon: Fuel,
        description: t('components.sidebar.items.fuel.desc'),
        // Legacy `/fuel` is the prefix of the new `/fuel/...` children, so
        // without exact-match the parent would always highlight when any
        // child route is active.
        exact: true,
      },
      {
        label: t('components.sidebar.items.fuel-finder.label'),
        value: 'fuel-finder',
        href: '/fuel/finder',
        icon: Fuel,
        description: t('components.sidebar.items.fuel-finder.desc'),
      },
      // TEMP-HIDE #1444: Stop Ratings hidden from Fuel nav (route /fuel/ratings
      // + permissions intact). Re-enable by uncommenting this entry.
      // {
      //   label: 'Stop Ratings',
      //   value: 'fuel-ratings',
      //   href: '/fuel/ratings',
      //   icon: Fuel,
      //   description: 'Truck stop ratings',
      // },
      { label: t('components.sidebar.items.manual-fuel.label'), value: 'manual-fuel', href: '/fuel/manual-fuel', icon: Fuel, description: t('components.sidebar.items.manual-fuel.desc') },
      // #1747 — EFS / fuel-card import triage. Gated: efs_integration feature + fuel permission.
      { label: t('components.sidebar.items.fuel-cards-import.label'), value: 'fuel-cards-import', href: '/financial/fuel-cards-import', icon: Fuel, description: t('components.sidebar.items.fuel-cards-import.desc') },
      // TEMP-HIDE #1444: Fuel Cards hidden from Fuel nav (route /financial/fuel-cards
      // + permissions intact). Re-enable by uncommenting this entry.
      // { label: 'Fuel Cards', value: 'fuel-cards-fin', href: '/financial/fuel-cards', icon: Fuel, description: 'Cards & surcharge' },
    ],
  },
  // ATL-22 — canonical WAYP-945 Dispatch group restored immediately after
  // Dashboard. Children:
  //   1. Dispatch Planner — relocated from a standalone top-level group
  //   2. Quotes — re-homed from the renamed Dispatch Planner group above
  //   3. Tender — placeholder, hidden via hiddenNavKeys until separate ticket
  //   4. Dispatch — variant-aware chooser landing at /dispatch-loads
  //   5. Dispatch History — placeholder, hidden via hiddenNavKeys
  //   6. Dispatch Calendar — placeholder, hidden via hiddenNavKeys
  //   7. Recurring Load — variant-scoped recurring loads surface
  // Parent visibility uses the standard "all-children-hidden → parent-hidden"
  // sweep — when none of the 4 mod_*_main are on AND Quotes/enableQuotes
  // path is off, the entire group disappears.
  {
    key: 'dispatch',
    label: t('components.sidebar.groups.dispatch.label'),
    icon: Truck,
    children: [
      // #1156 — Dispatch Planner relocated here from a standalone top-level
      // group above. Preserves the original nav value, route, and gates so
      // existing permission / feature mappings keep working unchanged.
      {
        label: t('components.sidebar.items.dispatch.label'),
        value: 'dispatch',
        href: '/dispatch',
        icon: Truck,
        description: t('components.sidebar.items.dispatch.desc'),
        exact: true,
      },
      {
        label: t('components.sidebar.items.quotes.label'),
        value: 'quotes',
        href: '/quotes',
        icon: FileText,
        description: t('components.sidebar.items.quotes.desc'),
      },
      {
        label: t('components.sidebar.items.dispatch-loads-tender.label'),
        value: 'dispatch-loads-tender',
        href: '/dispatch-loads/tender',
        icon: Send,
        description: t('components.sidebar.items.dispatch-loads-tender.desc'),
      },
      // ATL-001415 — AI Email Intake review queue. Hidden unless the
      // `ai_email_load_creation` feature is on and the user has email_intake:view.
      {
        label: t('components.sidebar.items.email-intake.label'),
        value: 'email-intake',
        href: '/dispatch/email-intake',
        icon: MailSearch,
        description: t('components.sidebar.items.email-intake.desc'),
      },
      // DAT – ATL-001428 (B1): Find Loads on the DAT load board. Gated by the
      // `dat_loadboard` feature (NAV_VALUE_TO_FEATURE_KEY), its `search`
      // preference (hiddenNavKeys) and `loads:create` (the search API's guard).
      {
        label: t('components.sidebar.items.dat-find-loads.label'),
        value: 'dat-find-loads',
        href: '/dispatch/dat/find-loads',
        icon: Search,
        description: t('components.sidebar.items.dat-find-loads.desc'),
      },
      // DAT Margin Calculator: price a load between shipper and carrier.
      // Gated by `dat_loadboard`, its `margin_calculator` preference and the
      // Type of Business `priceLoads` capability (hiddenNavKeys).
      {
        label: t('components.sidebar.items.dat-margin-calculator.label'),
        value: 'dat-margin-calculator',
        href: '/dispatch/dat/margin-calculator',
        icon: Calculator,
        description: t('components.sidebar.items.dat-margin-calculator.desc'),
      },
      // #1635 — FarEye inbound API Tenders. Sibling of the USPS EDI Tenders
      // screen, placed under Dispatch. Two-layer visibility gate:
      //   1. `api_tendering` enterprise feature → NAV_VALUE_TO_FEATURE_KEY
      //      below (hidden by the hiddenNavKeys feature-loop when OFF). The
      //      org configures the FarEye integration as part of enabling that
      //      feature (requiresSetup: true), so the feature flag is the
      //      operator-facing "integration is set up" signal.
      //   2. `edi:view` permission → NAV_VALUE_TO_PERMISSION below (matches
      //      the server router guard).
      {
        label: t('components.sidebar.items.api-tenders.label'),
        value: 'api-tenders',
        href: '/dispatch/api-tenders',
        icon: Mailbox,
        description: t('components.sidebar.items.api-tenders.desc'),
        // Exact match so the History route (a path prefix of this one) doesn't
        // also highlight this entry.
        exact: true,
      },
      // #1635 — API Tender History (past tenders). Sibling of the queue entry
      // above, mirroring EDI Tenders / EDI History. Same two-layer gate
      // (api_tendering feature + edi:view permission).
      {
        label: t('components.sidebar.items.api-tenders-history.label'),
        value: 'api-tenders-history',
        href: '/dispatch/api-tenders/history',
        icon: Mailbox,
        description: t('components.sidebar.items.api-tenders-history.desc'),
      },
      // 2026-07-29 — "Dispatch" (Dispatch Loads) and "Dispatch History" nav
      // entries removed: both surfaces now live inside the Dispatch Planner —
      // Weekly Rolling embeds the loads page (Loads/Customer Portal/Recurring
      // tabs) and the History view embeds LoadsHistoryPage. The underlying
      // routes (/dispatch-loads/:variant, /dispatch-loads/history) are KEPT so
      // deep-links and the create form's Cancel/Save redirects still resolve;
      // only the redundant nav links are dropped. (The NAV_VALUE_TO_FEATURE_KEY
      // / NAV_VALUE_TO_PERMISSION entries for these values are now inert.)
      // ATL-000802 — the "Dispatch Calendar" entry (href /dispatch-loads/calendar)
      // is removed. No such route was ever registered: the path fell into the
      // `/dispatch-loads/:variant` catch-all, failed to resolve a variant and
      // bounced to /dashboard. The calendar itself lives in the Dispatch
      // Planner's view switcher (`/dispatch?view=calendar`), so this follows the
      // 2026-07-29 precedent above — the redundant nav link goes, the surface
      // stays where operators already reach it.
      // 2026-07-28 — "Recurring Load" moved OUT of this dropdown into the
      // Dispatch Planner's Weekly Rolling window as a left-side tab
      // (DispatchLoadsPage tab group). The `/dispatch-loads/:variant/recurring`
      // route is kept as a deep-link. See DispatchLoadsPage `adminView`.
    ],
  },
  // WAYP-505 — top-level USPS Carrier group. Sibling to Operations (not a
  // child of it). Gated by the mod_usps_main enterprise feature + the usps
  // permission module. When the feature is OFF the entire group (including
  // its dropdown items) is removed from the nav tree.
  //
  // #1307 (Andrew 2026-05-26): reshaped from a flat `children` list into a
  // 3-column mega-panel via `columnGroups`. Same `NavChild` records, same
  // `value` strings (so every NAV_VALUE_TO_FEATURE_KEY / NAV_VALUE_TO_PERMISSION
  // / hiddenNavKeys gate keeps working unchanged) — only the grouping
  // changes. Columns:
  //   - Types     : master-data definitions
  //   - Planning  : pre-load operational records
  //   - Loads     : day-of execution surfaces
  // Multi-column infrastructure (NavColumnGroup type + visibility filter)
  // already exists — see the Settings group below for the reference impl.
  {
    key: 'uspsCarrier',
    label: t('components.sidebar.groups.uspsCarrier.label'),
    icon: Mailbox,
    columnGroups: [
      {
        key: 'types',
        header: t('components.sidebar.groups.uspsCarrier.columns.types'),
        children: [
          {
            label: t('components.sidebar.items.usps-mail-class-types.label'),
            value: 'usps-mail-class-types',
            href: '/usps/mail-class-types',
            icon: Send,
            description: t('components.sidebar.items.usps-mail-class-types.desc'),
          },
          {
            label: t('components.sidebar.items.usps-mail-purpose-types.label'),
            value: 'usps-mail-purpose-types',
            href: '/usps/mail-purpose-types',
            icon: Flag,
            description: t('components.sidebar.items.usps-mail-purpose-types.desc'),
          },
          // WAYP-681 (umbrella #677) — Frequency Types. Two-layer visibility
          // gate handled via NAV_VALUE_TO_FEATURE_KEY (mod_usps_main) +
          // hiddenNavKeys (hide_contracts_and_trips preference). Permission
          // gated on frequency_types:view via NAV_VALUE_TO_PERMISSION above.
          // URL is /usps/frequency-types; legacy /settings/frequency-types
          // URL redirects via App.tsx. #1307: label pluralized to
          // "Frequency Types" (was "Frequency Type").
          {
            label: t('components.sidebar.items.frequency-types.label'),
            value: 'frequency-types',
            href: '/usps/frequency-types',
            icon: CalendarOff,
            description: t('components.sidebar.items.frequency-types.desc'),
          },
          // Process EDI (#1755) moved out of this column into Loads, next to
          // Tenders and FourKites Tracking — it is an EDI exception-review
          // console, not a master-data "type" like the rest of this column.
          // WAYP-504 — Slips relocated from Settings > System Settings. The
          // existing two-layer gate (mod_usps_main + enable_slip preference)
          // in `hiddenNavKeys` below still hides this entry when either is
          // off.
          {
            label: t('components.sidebar.items.slips.label'),
            value: 'slips',
            href: '/settings/slips',
            icon: FileText,
            description: t('components.sidebar.items.slips.desc'),
          },
        ],
      },
      {
        key: 'planning',
        header: t('components.sidebar.groups.uspsCarrier.columns.planning'),
        children: [
          // USPS-CONTRACTS-06 (2026-04-29) — Contracts re-homed from Operations
          // into the USPS Carrier nav group. URL reverted 2026-04-30 per
          // testing feedback: path is `/usps/contracts` (the original
          // WAYP-177 path); the briefly-shipped `/usps-carrier/contracts`
          // redirects there.
          // #1216 USPS Legacy Cleanup (Andrew 2026-05-21): the original
          // "universally available" decision was reversed — Contracts only
          // lives under this dropdown, and keeping it ungated left the
          // entire USPS Carrier group visible even when `mod_usps_main` was
          // OFF. Gated on `mod_usps_main` via NAV_VALUE_TO_FEATURE_KEY
          // above. Permission gating uses `contracts:view` (registered in
          // USPS-CONTRACTS-02) via NAV_VALUE_TO_PERMISSION above.
          {
            label: t('components.sidebar.items.contracts.label'),
            value: 'contracts',
            href: '/usps/contracts',
            icon: Handshake,
            description: t('components.sidebar.items.contracts.desc'),
          },
          // WAYP-764 (umbrella) — USPS Trips V1-parity rebuild. Sibling to
          // Contracts (the trip belongs to a contract). Permission scope is
          // `trips:view` (registered in slice 02) via NAV_VALUE_TO_PERMISSION.
          // Two-layer feature gate: layer-1 `mod_usps_main` via
          // NAV_VALUE_TO_FEATURE_KEY above; layer-2 `hide_contracts_and_trips`
          // preference handled in `hiddenNavKeys` below alongside the
          // Frequency Types entry. The legacy `Operations → USPS → Trips`
          // tab is removed in the same slice.
          {
            label: t('components.sidebar.items.usps-trips.label'),
            value: 'usps-trips',
            href: '/usps/trips',
            icon: Truck,
            description: t('components.sidebar.items.usps-trips.desc'),
          },
          // WAYP-927 — Driver Routes (V1 /bids/ → V2 /driver-routes/ port).
          // Permission gate: `driver_routes:view` via NAV_VALUE_TO_PERMISSION.
          // Feature gate: `mod_usps_main` via NAV_VALUE_TO_FEATURE_KEY (the
          // standalone `mod_driver_routes` feature was retired in #1216 USPS
          // Legacy Cleanup, 2026-05-21). The trips that drive this module
          // come from USPS Trips, so the entry lives under the USPS Carrier
          // nav group.
          // #1307 (Andrew 2026-05-26): label stays as 'Driver Routes' —
          // it's already dynamically substituted at render time from the
          // org's `bids_module_name` Enterprise preference; a static
          // rename would clobber the dynamic value.
          {
            label: t('components.sidebar.items.driver-routes.label'),
            value: 'driver-routes',
            href: '/usps/driver-routes',
            icon: Route,
            description: t('components.sidebar.items.driver-routes.desc'),
          },
          // Umbrella #966 (slice f/i) — USPS Recurring Loads. Sibling to
          // Trips (each recurring load aggregates trips into a per-day plan).
          // Three-layer gate:
          //   1. `mod_usps_main` enterprise feature  → NAV_VALUE_TO_FEATURE_KEY
          //   2. `recurring:view` permission         → NAV_VALUE_TO_PERMISSION
          //   3. `recurring_loads` preference (V1-mirrored toggle) →
          //      `hiddenNavKeys` below. Reads
          //      `mod_usps_main.configJson.recurring_loads`. Default OFF.
          // All three gates must pass for the entry to render. The route at
          // /usps/recurring-loads stays accessible to direct URLs (matching
          // how /usps/contracts + /usps/trips behave); the page-level
          // FeatureGate surfaces an "feature not enabled" card otherwise.
          {
            label: t('components.sidebar.items.usps-recurring-loads.label'),
            value: 'usps-recurring-loads',
            href: '/usps/recurring-loads',
            icon: Repeat,
            description: t('components.sidebar.items.usps-recurring-loads.desc'),
          },
          // USPS Renewal (#1922) — annual renewal cycle. Under Planning per the
          // review. Two gates: `mod_usps_main` feature (NAV_VALUE_TO_FEATURE_KEY)
          // + `usps_renewals:view` permission (NAV_VALUE_TO_PERMISSION).
          {
            label: t('components.sidebar.items.usps-renewals.label'),
            value: 'usps-renewals',
            href: '/usps/renewals',
            icon: CalendarDays,
            description: t('components.sidebar.items.usps-renewals.desc'),
          },
        ],
      },
      // Atlas cmsrm3ev8007v11agrxpqdu6w — new "Tenders" section, positioned
      // immediately after Planning. Collects the two USPS tender/EDI surfaces
      // that previously sat in Types (Process EDI) and Loads (Tenders). Both
      // entries keep their `value`, so every feature/permission gate below is
      // unchanged by the move.
      {
        key: 'tenders',
        header: t('components.sidebar.groups.uspsCarrier.columns.tenders'),
        children: [
          // Process EDI (#1755) — USPS EDI exception-review console, relocated
          // from the Types column (it is not a master-data "type"). Two-layer
          // gate: `edi` enterprise feature (NAV_VALUE_TO_FEATURE_KEY above) +
          // `usps_process_edi:view` permission (NAV_VALUE_TO_PERMISSION above).
          {
            label: t('components.sidebar.items.usps-process-edi.label'),
            value: 'usps-process-edi',
            href: '/usps/process-edi',
            icon: FileSearch,
            description: t('components.sidebar.items.usps-process-edi.desc'),
          },
          // USPS Tenders V2 — quotes-based tender queue (isTender=true,
          // module='usps'), relocated from the Loads column. Two-layer gate:
          //   1. `mod_usps_main` enterprise feature (NAV_VALUE_TO_FEATURE_KEY)
          //   2. `usps_tenders:view` permission (NAV_VALUE_TO_PERMISSION)
          {
            label: t('components.sidebar.items.usps-tenders.label'),
            value: 'usps-tenders',
            href: '/usps/tenders',
            icon: FileText,
            description: t('components.sidebar.items.usps-tenders.desc'),
          },
        ],
      },
      {
        key: 'loads',
        header: t('components.sidebar.groups.uspsCarrier.columns.loads'),
        children: [
          // #2302 (Andrew 2026-08-06) — USPS Loads is hidden from the nav; the
          // USPS Planner below supersedes it. The page itself still exists and
          // `/usps/loads` now redirects to `/usps/planner` (App.tsx) so old
          // bookmarks land somewhere useful. Kept commented rather than deleted
          // so it can be restored without re-deriving the gates.
          // {
          //   label: t('components.sidebar.items.usps-loads.label'),
          //   value: 'usps-loads',
          //   href: '/usps/loads',
          //   icon: Package,
          //   description: t('components.sidebar.items.usps-loads.desc'),
          // },
          // #2302 — USPS Planner: USPS-only multi-view planner (mirror of the
          // Dispatch Planner). Same two-layer gate as USPS Loads.
          {
            label: t('components.sidebar.items.usps-planner.label'),
            value: 'usps-planner',
            href: '/usps/planner',
            icon: GanttChartSquare,
            description: t('components.sidebar.items.usps-planner.desc'),
          },
          // #2302 (Andrew 2026-08-06) — USPS Load History hidden alongside USPS
          // Loads; `/usps/loads/history` redirects to `/usps/planner` (App.tsx).
          // {
          //   label: t('components.sidebar.items.usps-load-history.label'),
          //   value: 'usps-load-history',
          //   href: '/usps/loads/history',
          //   icon: History,
          //   description: t('components.sidebar.items.usps-load-history.desc'),
          // },
          // USPS 5500 (#1556) — slip-reason inquiry grid. Three gates:
          //   1. `mod_usps_main` enterprise feature (NAV_VALUE_TO_FEATURE_KEY)
          //   2. `usps_5500:view` permission (NAV_VALUE_TO_PERMISSION)
          //   3. `organization.usps500` slip-reason preference set
          //      (`hiddenNavKeys` below).
          {
            label: t('components.sidebar.items.usps-5500.label'),
            value: 'usps-5500',
            href: '/usps/5500',
            icon: FileSpreadsheet,
            description: t('components.sidebar.items.usps-5500.desc'),
          },
          // WAYP-814 follow-up — USPS Carrier > Quotes. Mounts the same
          // QuotesPage / QuoteWizard pair as the dispatch surface but with
          // `defaultModule="usps"` (see App.tsx routes). Internally the
          // pair resolves the matching long key (`mod_usps_main`) for the
          // Rate Type / Fuel Rate / Quotable Load Status filters and uses
          // `/usps/quotes` as its back-nav base. Gated on three layers:
          //   1. `mod_usps_main` enterprise feature (NAV_VALUE_TO_FEATURE_KEY)
          //   2. `quotes:view` permission (NAV_VALUE_TO_PERMISSION)
          //   3. `companyPreferences.enableQuotesUspsCarrier`
          //      (Settings → Company Preferences → USPS tab → Enable Quote
          //       Workflow) — added in `hiddenNavKeys` below; mirrors the
          //      Dispatch-surface gate via `enableQuotes`.
          // #1307 (Andrew 2026-05-26): moved into the Loads column.
          {
            label: t('components.sidebar.items.usps-quotes.label'),
            value: 'usps-quotes',
            href: '/usps/quotes',
            icon: FileText,
            description: t('components.sidebar.items.usps-quotes.desc'),
          },
          // Process EDI + Tenders moved out of this column into the new Tenders
          // section above (Atlas cmsrm3ev8007v11agrxpqdu6w).
        ],
      },
      // Tracking (ATL-000727) — its own column between Loads and USPS
      // Reconciliation, holding FourKites Tracking (which briefly lived in the
      // reconciliation column as "Operations" — Atlas cmsrm3ev8007v11agrxpqdu6w,
      // reshaped by this ticket). The route stays
      // `/operations/fourkites-monitoring` — same precedent as Slips, which
      // lives under USPS Carrier while keeping its `/settings/slips` URL, so
      // existing bookmarks and the App.tsx route are untouched.
      // Three gates, all preserved because they key off the nav `value`:
      //   1. `mod_usps_main` enterprise feature (NAV_VALUE_TO_FEATURE_KEY)
      //   2. `edi:view` permission (NAV_VALUE_TO_PERMISSION)
      //   3. org has ≥1 FourKites-enabled EDI trading partner
      //      (`hiddenNavKeys`, fail-safe hidden while loading)
      {
        key: 'tracking',
        header: t('components.sidebar.groups.uspsCarrier.columns.tracking'),
        children: [
          {
            label: t('components.sidebar.items.fourkites-monitoring.label'),
            value: 'fourkites-monitoring',
            href: '/operations/fourkites-monitoring',
            icon: Send,
            description: t('components.sidebar.items.fourkites-monitoring.desc'),
          },
        ],
      },
      // Reconcile Slips (#1564) — column placed after the Loads + Tracking
      // columns. Holds the Reconcile Slips SSRM grid (ALL USPS-variant load
      // slips). Two gates: `mod_usps_main` feature + `reconcile_slips:view`
      // perm (the `usps500` slip-reason preference no longer gates this
      // module).
      // ATL-000727 — the header is "USPS Reconciliation" again (the
      // cmsrm3ev8007v11agrxpqdu6w "Operations" rename is reverted, and
      // FourKites Tracking moved out to the Tracking column above).
      {
        key: 'uspsReconciliation',
        header: t('components.sidebar.groups.uspsCarrier.columns.uspsReconciliation'),
        children: [
          {
            label: t('components.sidebar.items.reconcile-slips.label'),
            value: 'reconcile-slips',
            href: '/usps/reconcile-slips',
            icon: Scale,
            description: t('components.sidebar.items.reconcile-slips.desc'),
          },
          // CLEAR Rates (#1652) — V1 `/usps-invoicing/clear-rates`. Imported
          // USPS CLEAR rate sheets. Two gates: `mod_usps_main` feature +
          // `clear_rates:view` perm, plus the `usps500` slip-reason preference
          // (same third gate as USPS 5500 — see `hiddenNavKeys`).
          {
            label: t('components.sidebar.items.clear-rates.label'),
            value: 'clear-rates',
            href: '/usps/clear-rates',
            icon: Receipt,
            description: t('components.sidebar.items.clear-rates.desc'),
          },
          // FourKites weekly compliance scores. Gated on `mod_usps_main` +
          // `fourkites_compliance:view` (NOT the usps500 preference).
          {
            label: t('components.sidebar.items.compliance-scores.label'),
            value: 'compliance-scores',
            href: '/usps/compliance-scores',
            icon: GaugeCircle,
            description: t('components.sidebar.items.compliance-scores.desc'),
          },
          // #1763 — USPS Voucher Import. Sibling of CLEAR Rates. Two gates:
          // `mod_usps_main` feature + `usps_vouchers:view` perm, plus the
          // `usps500` slip-reason preference (same third gate as CLEAR Rates —
          // see `hiddenNavKeys`).
          {
            label: t('components.sidebar.items.vouchers.label'),
            value: 'vouchers',
            href: '/usps/vouchers',
            icon: Receipt,
            description: t('components.sidebar.items.vouchers.desc'),
          },
          // #1770 — USPS Reconciliation 3-tab page. Sibling of Voucher Import.
          // Same gates: `mod_usps_main` feature + `usps_reconciliation:view` perm
          // + the `usps500` slip-reason preference (see `hiddenNavKeys`).
          {
            label: t('components.sidebar.items.usps-reconciliation.label'),
            value: 'usps-reconciliation',
            href: '/usps/reconciliation',
            icon: Scale,
            description: t('components.sidebar.items.usps-reconciliation.desc'),
          },
          // #1770 — No Miles Reconciliation (own page, V1 parity). Same gates as
          // the rest of the USPS Reconciliation group.
          {
            label: t('components.sidebar.items.usps-no-miles-reconciliation.label'),
            value: 'usps-no-miles-reconciliation',
            href: '/usps/no-miles-reconciliation',
            icon: Scale,
            description: t('components.sidebar.items.usps-no-miles-reconciliation.desc'),
          },
        ],
      },
    ],
  },
  // WAYP-945 (pivot 2026-05-11) — per-variant top-level groups, each with
  // {Quotes, <Variant Loads>, <Variant> History, <Variant> Calendar}.
  // History + Calendar children are hidden via `hiddenNavKeys` until those
  // tickets land. Each variant load entry routes to the shared
  // `/dispatch-loads/<variant>` surface this ticket builds.
  {
    key: 'intermodal',
    label: t('components.sidebar.groups.intermodal.label'),
    icon: Boxes,
    children: [
      {
        label: t('components.sidebar.items.intermodal-quotes.label'),
        value: 'intermodal-quotes',
        href: '/intermodal/quotes',
        icon: FileText,
        description: t('components.sidebar.items.intermodal-quotes.desc'),
      },
      {
        label: t('components.sidebar.items.dispatch-loads-intermodal.label'),
        value: 'dispatch-loads-intermodal',
        href: '/dispatch-loads/intermodal',
        icon: Truck,
        description: t('components.sidebar.items.dispatch-loads-intermodal.desc'),
      },
      {
        // WAYP-1246 — variant-aware History (Intermodal).
        label: t('components.sidebar.items.intermodal-history.label'),
        value: 'intermodal-history',
        href: '/dispatch-loads/history?variant=intermodal',
        icon: ClipboardList,
        description: t('components.sidebar.items.intermodal-history.desc'),
      },
      // 2026-07-28 — Recurring Load moved into the loads grid's tab group.
    ],
  },
  {
    key: 'fullTruckloadBrokerage',
    label: t('components.sidebar.groups.fullTruckloadBrokerage.label'),
    icon: Handshake,
    children: [
      {
        label: t('components.sidebar.items.ftl-brokerage-quotes.label'),
        value: 'ftl-brokerage-quotes',
        href: '/freight/quotes',
        icon: FileText,
        description: t('components.sidebar.items.ftl-brokerage-quotes.desc'),
      },
      {
        label: t('components.sidebar.items.ftl-brokerage.label'),
        value: 'ftl-brokerage',
        href: '/dispatch-loads/ftl-brokerage',
        icon: Truck,
        description: t('components.sidebar.items.ftl-brokerage.desc'),
      },
      {
        // WAYP-1246 — variant-aware History (FTL Brokerage).
        label: t('components.sidebar.items.ftl-brokerage-history.label'),
        value: 'ftl-brokerage-history',
        href: '/dispatch-loads/history?variant=ftl-brokerage',
        icon: ClipboardList,
        description: t('components.sidebar.items.ftl-brokerage-history.desc'),
      },
      // 2026-07-28 — Recurring Load moved into the loads grid's tab group.
    ],
  },
  {
    key: 'livestock',
    label: t('components.sidebar.groups.livestock.label'),
    icon: Truck,
    children: [
      {
        label: t('components.sidebar.items.livestock-quotes.label'),
        value: 'livestock-quotes',
        href: '/livestock/quotes',
        icon: FileText,
        description: t('components.sidebar.items.livestock-quotes.desc'),
      },
      {
        label: t('components.sidebar.items.livestock.label'),
        value: 'livestock',
        href: '/dispatch-loads/livestock',
        icon: Truck,
        description: t('components.sidebar.items.livestock.desc'),
      },
      {
        // WAYP-1246 — variant-aware History (Livestock).
        label: t('components.sidebar.items.livestock-history.label'),
        value: 'livestock-history',
        href: '/dispatch-loads/history?variant=livestock',
        icon: ClipboardList,
        description: t('components.sidebar.items.livestock-history.desc'),
      },
      // 2026-07-28 — Recurring Load moved into the loads grid's tab group.
    ],
  },
  // WAYP-952 — V1-parity Rating sidebar group. Surfaces three per-customer
  // / per-driver rate-override sub-pages. The whole group is hidden when
  // the `customer:rating` enterprise feature is OFF (handled per-child
  // via NAV_VALUE_TO_FEATURE_KEY above — when all three children are
  // hidden, the parent group collapses out automatically).
  {
    key: 'rating',
    label: t('components.sidebar.groups.rating.label'),
    icon: DollarSign,
    children: [
      // Main rate-data sub-page — exactly one of these renders based on
      // customer:rating.configJson.rate_type. hiddenNavKeys hides the
      // two that don't match.
      { label: t('components.sidebar.items.billing-rates.label'), value: 'billing-rates', href: '/rating/billing-rates', icon: Receipt, description: t('components.sidebar.items.billing-rates.desc') },
      { label: t('components.sidebar.items.lane-location-rates.label'), value: 'lane-location-rates', href: '/rating/lane-location-rates', icon: Receipt, description: t('components.sidebar.items.lane-location-rates.desc') },
      { label: t('components.sidebar.items.lane-city-rates.label'), value: 'lane-city-rates', href: '/rating/lane-city-rates', icon: Receipt, description: t('components.sidebar.items.lane-city-rates.desc') },
      // FSC + Accessorial toggle on their own enterprise-feature
      // preferences (enable_fsc_per_customer / enable_accessorial_rates).
      { label: t('components.sidebar.items.fsc-rates.label'), value: 'fsc-rates', href: '/rating/fsc-rates', icon: Fuel, description: t('components.sidebar.items.fsc-rates.desc') },
      { label: t('components.sidebar.items.accessorial-rates.label'), value: 'accessorial-rates', href: '/rating/accessorial-rates', icon: ReceiptText, description: t('components.sidebar.items.accessorial-rates.desc') },
    ],
  },
  {
    key: 'finance',
    label: t('components.sidebar.groups.finance.label'),
    icon: Banknote,
    children: [
      { label: t('components.sidebar.items.invoices.label'), value: 'invoices', href: '/invoices', icon: Receipt, description: t('components.sidebar.items.invoices.desc') },
      // #1087: Custom Charges / Credits — Finance menu V1→V2 foundation.
      // Sits between Invoices and Settlements per the issue body.
      { label: t('components.sidebar.items.custom-charges.label'), value: 'custom-charges', href: '/financial/custom-charges', icon: FileSpreadsheet, description: t('components.sidebar.items.custom-charges.desc') },
      // Other Charges or Reimbursements — V1 `Invoicing → Other Charges or
      // Reimbursements` parity. Lists per-load LoadOtherCharge rows across
      // every load in the tenant.
      { label: t('components.sidebar.items.other-charges-reimbursements.label'), value: 'other-charges-reimbursements', href: '/financial/other-charges-reimbursements', icon: Receipt, description: t('components.sidebar.items.other-charges-reimbursements.desc') },
      { label: t('components.sidebar.items.settlements.label'), value: 'settlements', href: '/settlements', icon: Banknote, description: t('components.sidebar.items.settlements.desc') },
      // #1154 — Deductions & Reimbursements (V1 parity 3-tab page). Gated
      // on the same `settlements:view` scope as Settlements (V1 also keys
      // both off the Settlements module).
      { label: t('components.sidebar.items.deductions.label'), value: 'deductions', href: '/deductions', icon: Scale, description: t('components.sidebar.items.deductions.desc') },
      { label: t('components.sidebar.items.received-payments.label'), value: 'received-payments', href: '/financial/received-payments', icon: DollarSign, description: t('components.sidebar.items.received-payments.desc') },
      // #1137 — Customer Statements & Collections. Hidden when the
      // `enable_statements_collections` enterprise feature is OFF for the
      // org (see NAV_VALUE_TO_FEATURE_KEY above).
      { label: t('components.sidebar.items.customer-statements.label'), value: 'customer-statements', href: '/financial/customer-statements', icon: Mail, description: t('components.sidebar.items.customer-statements.desc') },
      { label: t('components.sidebar.items.escrow_accounts.label'), value: 'escrow_accounts', href: '/financial/escrow', icon: Wallet, description: t('components.sidebar.items.escrow_accounts.desc') },
      { label: t('components.sidebar.items.driver-loans.label'), value: 'driver-loans', href: '/financial/driver-loans', icon: CreditCard, description: t('components.sidebar.items.driver-loans.desc') },
      { label: t('components.sidebar.items.work-orders.label'), value: 'work-orders', href: '/financial/work-orders', icon: Wrench, description: t('components.sidebar.items.work-orders.desc') },
      // WAYP-389: Purchase Orders list page (V2 port of V1 /purchase-order).
      { label: t('components.sidebar.items.purchase-orders.label'), value: 'purchase-orders', href: '/purchase-orders', icon: ShoppingCart, description: t('components.sidebar.items.purchase-orders.desc') },
      // WAYP-392: Expenses module (V1 parity port)
      { label: t('components.sidebar.items.expenses.label'), value: 'expenses', href: '/financial/expenses', icon: ReceiptText, description: t('components.sidebar.items.expenses.desc') },
      // Report Catalog hidden — page is non-functional; re-enable once the feature ships.
      // { label: 'Report Catalog', value: 'report-catalog', href: '/financial/report-catalog', icon: BookOpen, description: 'Canned reports' },
    ],
  },
  {
    key: 'reports',
    label: t('components.sidebar.groups.reports.label'),
    icon: BarChart3,
    href: '/reports',
    value: 'reports',
    // #1837 — Reports is a plain click-to-navigate nav item. The earlier
    // #1573 hover-intent navigation (navigateOnHover) was removed because
    // merely mousing over the button navigated unexpectedly.
  },
  {
    key: 'support',
    label: t('components.sidebar.groups.support.label'),
    icon: LifeBuoy,
    href: '/support',
    value: 'support',
  },
  {
    key: 'settings',
    label: t('components.sidebar.groups.settings.label'),
    icon: Settings,
    // #1319 — palette mode turns the Settings mega-panel into a searchable
    // ⌘+K-style command palette. A search input is rendered at the top of
    // the dropdown; fuzzy matches narrow the columns live. The global
    // Cmd+K (Ctrl+K on Windows) shortcut, wired in `HorizontalNav`,
    // opens this dropdown and focuses the search input from anywhere in
    // the app.
    palette: true,
    // WAYP-731 (2026-04-30) — Settings restructured into four named groups
    // (General / Equipment / Drivers / System Settings). Per-item permission
    // and feature-flag gates are unchanged; only grouping/order moves.
    // "Onboard Customer" entry removed from the menu in this pass; the
    // /admin/onboard-customer route still exists for direct navigation.
    columnGroups: [
      // WAYP-681 follow-up — the previously-introduced "USPS" column under
      // Settings was reverted at Jorge's request. Frequency Type now lives
      // under the top-level USPS Carrier nav group; USPS Carrier (settings)
      // moves back into System Settings below. The `mod_usps_main` /
      // `hide_contracts_and_trips` gates still apply because the nav-value
      // mappings above are nav-location-independent.
      {
        key: 'general',
        header: t('components.sidebar.groups.settings.columns.general'),
        // #1322 — per-column icon tint. Five tints chosen by design to map to
        // the five Settings columns: neutral warm (General), blue (Equipment),
        // green (Drivers), amber (Loads), purple (System).
        // #1345 — Tints now come from the shared `SETTINGS_COLUMN_TINTS` map
        // so the same hex powers both the icon swatch and the header accent
        // bar (no hex duplication).
        iconTint: SETTINGS_COLUMN_TINTS.General,
        children: [
          // #1509 — Rate Types + Fuel Rates relocated here from the Loads
          // column to match the 2026-06-03 design mock. They lead the
          // General column because rate cards and fuel-rate tables are
          // org-wide master data referenced by both Loads and Drivers
          // (not exclusive to Loads). Design shows Rate Types as the
          // first General item and Fuel Rates immediately below it.
          { label: t('components.sidebar.items.rate-types.label'), value: 'rate-types', href: '/settings/rate-types', icon: DollarSign, description: t('components.sidebar.items.rate-types.desc') },
          // Weight-Based Zone Rating — Zones master. Hidden unless the
          // `weight_based_zone_rating` enterprise feature is on (see the
          // `hiddenNavKeys` memo below).
          { label: t('components.sidebar.items.zones.label'), value: 'zones', href: '/settings/zones', icon: MapPin, description: t('components.sidebar.items.zones.desc') },
          { label: t('components.sidebar.items.fuel-rates.label'), value: 'fuel-rates', href: '/settings/fuel-rates', icon: Fuel, description: t('components.sidebar.items.fuel-rates.desc') },
          { label: t('components.sidebar.items.taxes.label'), value: 'taxes', href: '/settings/taxes', icon: Receipt, description: t('components.sidebar.items.taxes.desc') },
          { label: t('components.sidebar.items.terminals.label'), value: 'terminals', href: '/settings/terminals', icon: MapPin, description: t('components.sidebar.items.terminals.desc') },
          // #1344 — Handshake icon (was Building2): handshake reads as
          // "partner agreement" and disambiguates from other Building2 entries.
          { label: t('components.sidebar.items.trading-partners.label'), value: 'trading-partners', href: '/settings/trading-partners', icon: Handshake, description: t('components.sidebar.items.trading-partners.desc') },
          // #1344 — ReceiptText icon (was Receipt): pairs with HandCoins on
          // Other Charge / Reimbursement Types so Expense (outflow, lined
          // receipt bill) reads visually distinct from Reimbursement (inflow,
          // hand returning coins). Both items previously rendered identical
          // Receipt glyphs.
          { label: t('components.sidebar.items.expense-types.label'), value: 'expense-types', href: '/settings/expense-types', icon: ReceiptText, description: t('components.sidebar.items.expense-types.desc') },
          // #1534 — Work Order Types + Purchase Order Types relocated from
          // the removed Equipment column (#1532) into the General column's
          // "Types" cluster (alongside Expense Types and Custom Data Types).
          { label: t('components.sidebar.items.work-order-types.label'), value: 'work-order-types', href: '/settings/work-order-types', icon: Wrench, description: t('components.sidebar.items.work-order-types.desc') },
          // #1344 — ClipboardCheck icon (was ShoppingCart): clipboard-with-
          // check reads as "order/approval workflow" rather than a retail cart.
          { label: t('components.sidebar.items.purchase-order-types.label'), value: 'purchase-order-types', href: '/settings/purchase-order-types', icon: ClipboardCheck, description: t('components.sidebar.items.purchase-order-types.desc') },
          // #1344 — Braces icon (was ListTree): curly-braces glyph matches
          // the "custom field definitions" semantic better than a list tree.
          { label: t('components.sidebar.items.custom-data-types.label'), value: 'custom-data-types', href: '/settings/custom-data-types', icon: Braces, description: t('components.sidebar.items.custom-data-types.desc') },
          // V1 parity (Types → Custom Templates): maps JS Report documents to
          // template types (Invoice, Settlement, BOL, Confirmations, Work Sheet).
          { label: t('components.sidebar.items.custom-templates.label'), value: 'custom-templates', href: '/settings/custom-templates', icon: FileText, description: t('components.sidebar.items.custom-templates.desc') },
          // WAYP-837 — Location Types settings page (V1 parity). LocationType
          // is a child concept of the Locations module so it inherits the
          // `locations` permission key (no dedicated permission module).
          { label: t('components.sidebar.items.location-types.label'), value: 'location-types', href: '/settings/location-types', icon: MapPin, description: t('components.sidebar.items.location-types.desc') },
          // File Checklist moved here from System Settings — checklist configs
          // are a general per-org setup item, not an admin/system action.
          { label: t('components.sidebar.items.file-checklist.label'), value: 'file-checklist', href: '/settings/file-checklist', icon: ClipboardList, description: t('components.sidebar.items.file-checklist.desc') },
        ],
      },
      // #1532 — Equipment column removed entirely from the Settings palette.
      // Equipment Types now lives on the Equipment screen tabs (#1320); Work
      // Order Types + Purchase Order Types are still routable via their
      // /settings/* paths but no longer surfaced in the palette. The three
      // routes (equipment-types, work-order-types, purchase-order-types)
      // remain registered in App.tsx so deep-links keep resolving.
      {
        key: 'drivers',
        header: t('components.sidebar.groups.settings.columns.drivers'),
        iconTint: SETTINGS_COLUMN_TINTS.Drivers,
        children: [
          { label: t('components.sidebar.items.driver-expiry-types.label'), value: 'driver-expiry-types', href: '/settings/driver-expiry-types', icon: ShieldCheck, description: t('components.sidebar.items.driver-expiry-types.desc') },
          { label: t('components.sidebar.items.deduction-types.label'), value: 'deduction-types', href: '/settings/deduction-types', icon: Tag, description: t('components.sidebar.items.deduction-types.desc') },
          // #1344 — HandCoins icon (was Receipt): hand-with-coins conveys
          // "money returning" (reimbursement / inbound charge) and pairs
          // with the ReceiptText icon on Expense Types so the two items
          // are no longer visually identical.
          { label: t('components.sidebar.items.other-charge-types.label'), value: 'other-charge-types', href: '/settings/other-charge-types', icon: HandCoins, description: t('components.sidebar.items.other-charge-types.desc') },
          { label: t('components.sidebar.items.carrier-requirement-types.label'), value: 'carrier-requirement-types', href: '/settings/carrier-requirement-types', icon: ShieldCheck, description: t('components.sidebar.items.carrier-requirement-types.desc') },
          { label: t('components.sidebar.items.driver-rating-questions.label'), value: 'driver-rating-questions', href: '/settings/driver-rating-questions', icon: Star, description: t('components.sidebar.items.driver-rating-questions.desc') },
          { label: t('components.sidebar.items.time-off-types.label'), value: 'time-off-types', href: '/settings/time-off-types', icon: CalendarOff, description: t('components.sidebar.items.time-off-types.desc') },
          { label: t('components.sidebar.items.department-types.label'), value: 'department-types', href: '/settings/department-types', icon: Building2, description: t('components.sidebar.items.department-types.desc') },
        ],
      },
      {
        key: 'loads',
        header: t('components.sidebar.groups.settings.columns.loads'),
        iconTint: SETTINGS_COLUMN_TINTS.Loads,
        children: [
          // #1504 — Layers icon (was FolderOpen): stacked-layers glyph reads
          // as "categorization / grouping" and is the 6th icon from the
          // original mega-menu spec that #1344 missed. Stays distinct from
          // the other Loads-column icons (ListTodo, GitBranch, DollarSign,
          // Fuel) and from the generic FolderOpen used elsewhere.
          { label: t('components.sidebar.items.load-categories.label'), value: 'load-categories', href: '/settings/load-categories', icon: Layers, description: t('components.sidebar.items.load-categories.desc') },
          { label: t('components.sidebar.items.commodity-categories.label'), value: 'commodity-categories', href: '/settings/commodity-categories', icon: Layers, description: t('components.sidebar.items.commodity-categories.desc') },
          { label: t('components.sidebar.items.load-statuses.label'), value: 'load-statuses', href: '/settings/load-statuses', icon: ListTodo, description: t('components.sidebar.items.load-statuses.desc') },
          { label: t('components.sidebar.items.commission-rules.label'), value: 'commission-rules', href: '/settings/commission-rules', icon: DollarSign, description: t('components.sidebar.items.commission-rules.desc') },
          // TODO #1163 — Custom Dispatch Tabs nav entry hidden from the
          // Settings menu. Route + page kept registered so direct URLs
          // still resolve (`/settings/custom-dispatch-tabs`); the
          // per-module Weekly Rolling Window setting moves to Enterprise
          // Settings (see #1164). Re-add this entry when reviving the
          // standalone tab editor.
          { label: t('components.sidebar.items.routing-row-types.label'), value: 'routing-row-types', href: '/settings/routing-row-types', icon: GitBranch, description: t('components.sidebar.items.routing-row-types.desc') },
          // #1509 — Rate Types + Fuel Rates relocated from this column to
          // the General column to match the 2026-06-03 design mock. Both
          // entries are now the first two items under General; this column
          // retains its load-specific configuration items.
        ],
      },
      {
        key: 'systemSettings',
        header: t('components.sidebar.groups.settings.columns.systemSettings'),
        iconTint: SETTINGS_COLUMN_TINTS['System Settings'],
        children: [
          { label: t('components.sidebar.items.settings.label'), value: 'settings', href: '/settings', icon: Settings, description: t('components.sidebar.items.settings.desc') },
          { label: t('components.sidebar.items.company-info.label'), value: 'company-info', href: '/settings/company-info', icon: Building2, description: t('components.sidebar.items.company-info.desc') },
          // WAYP-803: V1 parity — /settings/preferences. General tab is live;
          // per-module tabs (Dispatch / USPS / Intermodal / FTL Brokerage / Livestock)
          // land in #804–#808.
          { label: t('components.sidebar.items.company-preferences.label'), value: 'company-preferences', href: '/settings/company-preferences', icon: SlidersHorizontal, description: t('components.sidebar.items.company-preferences.desc') },
          { label: t('components.sidebar.items.dashboard-permissions.label'), value: 'dashboard-permissions', href: '/settings/dashboard-permissions', icon: ShieldCheck, description: t('components.sidebar.items.dashboard-permissions.desc') },
          // "API Keys" relocated into Settings → General Settings as a
          // super-admin-only tab (/settings?tab=api-keys). The /settings/api-keys
          // route stays registered for deep links; only this nav entry is removed.
          { label: t('components.sidebar.items.audit-logs.label'), value: 'audit-logs', href: '/audit-logs', icon: ShieldCheck, description: t('components.sidebar.items.audit-logs.desc') },
          { label: t('components.sidebar.items.admin-integrations.label'), value: 'admin-integrations', href: '/admin/integrations', icon: Plug, description: t('components.sidebar.items.admin-integrations.desc'), superAdminOnly: true },
          // #1649: QuickBooks Online connect/disconnect, sync status, and the
          // entry point for sending invoices/bills/payments to QuickBooks.
          // (ATL-001415: AI Email Intake settings moved into Integrations above.)
          { label: t('components.sidebar.items.quickbooks-online.label'), value: 'quickbooks-online', href: '/settings/quickbooks-online', icon: Receipt, description: t('components.sidebar.items.quickbooks-online.desc') },
          // QBD ticket: QuickBooks Desktop (Web Connector) — integration setup,
          // QWC download, list caches. Hidden unless `quickbooks_desktop` is on
          // (see NAV_VALUE_TO_FEATURE_KEY).
          { label: t('components.sidebar.items.quickbooks-desktop.label'), value: 'quickbooks-desktop', href: '/settings/quickbooks-desktop', icon: Receipt, description: t('components.sidebar.items.quickbooks-desktop.desc') },
          // #1731: Google Calendar — per-user connect/disconnect; auto-syncs load
          // pickup/drop-off events. Hidden unless the `calendar_google` feature is on.
          { label: t('components.sidebar.items.google-calendar.label'), value: 'google-calendar', href: '/settings/google-calendar', icon: CalendarDays, description: t('components.sidebar.items.google-calendar.desc') },
          // #1740: Outlook Calendar — per-user connect/disconnect; hidden unless `calendar_outlook` is on.
          { label: t('components.sidebar.items.outlook-calendar.label'), value: 'outlook-calendar', href: '/settings/outlook-calendar', icon: CalendarDays, description: t('components.sidebar.items.outlook-calendar.desc') },
          { label: t('components.sidebar.items.admin-features.label'), value: 'admin-features', href: '/admin/features', icon: ToggleLeft, description: t('components.sidebar.items.admin-features.desc'), superAdminOnly: true },
          // #1216 USPS Legacy Cleanup (Andrew 2026-05-21): "USPS Carrier"
          // settings entry retired alongside its page + 8 ModuleSetting
          // fields (no V2 readers).
          // File Checklist relocated to the General column.
        ],
      },
    ],
  },
  ];
}


/* ------------------------------------------------------------------ */
/*  Portal-based dropdown for nav groups                               */
/*  Rendered at <body> level so it escapes overflow:auto clip context  */
/* ------------------------------------------------------------------ */

/**
 * #1319 — render helper for one settings link inside the dropdown.
 * Extracted so the search-filtered list and the standard column rendering
 * both share one cell visual.
 *
 * #1343 — Active-item visual treatment. The selected Settings item (e.g.
 * "Enterprise Features") gets the Pale-Blue surface + 3px Light-Blue
 * stripe + accent text. Tokens mirror the mobile-nav active row in this
 * same file (`borderLeft: '3px solid var(--vz-primary)'`,
 * `backgroundColor: 'var(--vz-primary-bg-subtle)'`,
 * `color: 'var(--vz-primary)'` — see the mobile expanded list around
 * lines 2390-2393) so the two surfaces stay byte-identical in colour.
 * The inactive row carries a `3px solid transparent` left border so the
 * text baseline doesn't jump when the selected item changes.
 */
function NavChildLink({
  child,
  iconTintBg,
  active,
  onSelect,
  // #1346 — palette grid coordinates. When present, the link participates
  // in the palette's ↑/↓/←/→ arrow-key navigation. Cards omit these props
  // when they live outside the grid (Recent rail / standard flat list);
  // those cards stay Tab-able but the arrow-grid handler skips them.
  paletteCol,
  paletteRow,
  // #1350 — when true, the item title renders bold (semibold 600) so the
  // Settings palette cards lead with a strong title and a regular-weight
  // description underneath. Standard flat-list nav dropdowns keep the
  // original lighter title weight (400 / 500 active) untouched.
  palette = false,
}: {
  child: NavChild;
  iconTintBg: string;
  active: boolean;
  onSelect: () => void;
  paletteCol?: number;
  paletteRow?: number;
  palette?: boolean;
}) {
  const ChildIcon = child.icon;
  // #1346 — screen-reader label combines the card's title and description
  // using a middle-dot separator (U+00B7) so AT users hear "Trading
  // Partners · Manage trading partner records" instead of just the
  // title. Falls back to the title alone when no description is set.
  const ariaLabel = child.description
    ? `${child.label} · ${child.description}`
    : child.label;
  const isGridCard = paletteCol !== undefined && paletteRow !== undefined;
  return (
    <Link
      to={child.href}
      onClick={onSelect}
      aria-label={ariaLabel}
      data-palette-card={isGridCard ? '' : undefined}
      data-palette-col={isGridCard ? paletteCol : undefined}
      data-palette-row={isGridCard ? paletteRow : undefined}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        // #1343 — pad-left trimmed by 3px so the stripe occupies that
        // edge without shifting the icon/label rightward versus the
        // pre-#1343 layout. The transparent border on the inactive row
        // reserves the same 3px so width is identical in both states.
        padding: '8px 12px 8px 9px',
        borderRadius: '6px',
        borderLeft: active
          ? '3px solid var(--vz-primary)'
          : '3px solid transparent',
        textDecoration: 'none',
        color: active ? 'var(--vz-primary)' : 'var(--vz-heading-color)',
        backgroundColor: active ? 'var(--vz-primary-bg-subtle)' : 'transparent',
        transition: 'background-color 0.1s, color 0.1s, border-color 0.1s',
      }}
      onMouseEnter={(e) => {
        if (!active) e.currentTarget.style.backgroundColor = 'var(--vz-nav-hover-bg, rgba(0,0,0,0.04))';
      }}
      onMouseLeave={(e) => {
        if (!active) e.currentTarget.style.backgroundColor = 'transparent';
      }}
    >
      <div style={{
        width: '32px', height: '32px', borderRadius: '6px', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        backgroundColor: active ? 'var(--vz-primary-bg-subtle)' : iconTintBg,
      }}>
        <ChildIcon style={{ width: '16px', height: '16px', color: active ? 'var(--vz-primary)' : 'var(--vz-text-muted, #878a99)' }} />
      </div>
      <div style={{ minWidth: 0 }}>
        {/* #1350 — palette item titles render bold (600) regardless of
            active state so the Settings cards lead with a strong title
            paired with a regular-weight description. Non-palette flat
            lists keep the original lighter scale. */}
        <div style={{ fontSize: '13px', fontWeight: palette ? 600 : active ? 500 : 400, lineHeight: '1.3' }}>
          {child.label}
        </div>
        {child.description && (
          <div style={{ fontSize: '11px', color: 'var(--vz-text-muted, #878a99)', lineHeight: '1.3', marginTop: '1px' }}>
            {child.description}
          </div>
        )}
      </div>
    </Link>
  );
}

function NavGroupDropdown({
  group,
  Icon,
  active,
  isOpen,
  visibleChildren,
  visibleColumnGroups,
  openDropdown,
  closeDropdown,
  isChildActive,
  // #1321 — hover-peek vs. click-to-pin behaviour. When `pinned` is true
  // the panel ignores hover-out (only Esc / outside-click / re-click on
  // trigger closes it). When false it operates in peek mode with a
  // 200ms open delay and 300ms grace close.
  pinned,
  // #1319 — palette-mode search input ref. Owned by `HorizontalNav` so
  // the global ⌘+K shortcut can focus it.
  paletteInputRef,
  paletteQuery,
  setPaletteQuery,
  // #1320 — pre-resolved Recent rail items for the palette dropdown.
  recentItems,
}: {
  group: NavGroup;
  Icon: LucideIcon;
  active: boolean;
  isOpen: boolean;
  visibleChildren: NavChild[];
  visibleColumnGroups?: NavColumnGroup[];
  openDropdown: (label: string, opts?: { pinned?: boolean }) => void;
  closeDropdown: (label?: string) => void;
  isChildActive: (href: string) => boolean;
  pinned?: boolean;
  paletteInputRef?: React.RefObject<HTMLInputElement | null>;
  paletteQuery?: string;
  setPaletteQuery?: (q: string) => void;
  recentItems?: NavChild[];
}) {
  const { t } = useTranslation();
  const isPalette = !!group.palette;
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number } | null>(null);

  // Recalculate position whenever the dropdown opens. The panel normally
  // anchors to the trigger's left edge, but for wide multi-column groups
  // (the Settings mega-panel can run 4 × 220px = 880px) we'd overflow the
  // viewport when the trigger sits near the right edge of the nav. Estimate
  // the panel's expected width from `visibleColumnGroups` and shift left
  // when an overflow is detected, with an 8px safety margin.
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const colCount = visibleColumnGroups?.length ?? 0;
      const expectedWidth = colCount > 0 ? colCount * 220 : 260;
      const margin = 8;
      const maxLeft = window.innerWidth - expectedWidth - margin;
      const left = Math.max(margin, Math.min(rect.left, maxLeft));
      setDropdownPos({ top: rect.bottom + 1, left });
    }
  }, [isOpen, visibleColumnGroups]);

  // #1321 — Hover-peek behaviour. Palette groups open after a 200ms hover
  // delay (so the user doesn't trigger the panel by skimming the nav)
  // and close 300ms after hover-leave. Pinned palettes ignore
  // hover-leave entirely. Non-palette groups keep the legacy near-instant
  // behaviour (open immediately on enter, close after 120ms grace).
  const HOVER_OPEN_DELAY = isPalette ? 200 : 0;
  const HOVER_CLOSE_DELAY = isPalette ? 300 : 120;

  const handleMouseEnter = useCallback(() => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    if (pinned) return; // pinned palettes ignore hover signals
    if (HOVER_OPEN_DELAY > 0) {
      if (openTimer.current) clearTimeout(openTimer.current);
      openTimer.current = setTimeout(() => openDropdown(group.label), HOVER_OPEN_DELAY);
    } else {
      openDropdown(group.label);
    }
  }, [openDropdown, group.label, pinned, HOVER_OPEN_DELAY]);

  const handleMouseLeave = useCallback(() => {
    if (openTimer.current) clearTimeout(openTimer.current);
    if (pinned) return; // pinned: only Esc / outside-click / re-click closes
    closeTimer.current = setTimeout(() => closeDropdown(group.label), HOVER_CLOSE_DELAY);
  }, [closeDropdown, group.label, pinned, HOVER_CLOSE_DELAY]);

  // #1321 — click toggles pinned state on palette groups. First click
  // opens-and-pins; second click on the trigger closes the panel.
  // openDropdown / closeDropdown already manage `pinnedGroup` state at
  // the parent level — no extra `togglePin` call needed here, otherwise
  // we'd double-toggle and unpin immediately after opening.
  const handleTriggerClick = useCallback(() => {
    if (!isPalette) return; // non-palette groups keep their existing hover-only behaviour
    if (openTimer.current) clearTimeout(openTimer.current);
    if (closeTimer.current) clearTimeout(closeTimer.current);
    if (isOpen && pinned) {
      closeDropdown(group.label);
    } else {
      openDropdown(group.label, { pinned: true });
    }
  }, [isPalette, isOpen, pinned, openDropdown, closeDropdown, group.label]);

  // #1319 — palette search filtering. Score every visible child across the
  // mega-panel against the query and keep the columns intact (so users
  // still see "Settings → Drivers → Driver Expiries" context).
  const filteredColumnGroups = useMemo(() => {
    if (!isPalette || !visibleColumnGroups) return visibleColumnGroups;
    const q = (paletteQuery ?? '').trim();
    if (!q) return visibleColumnGroups;
    return visibleColumnGroups
      .map((col) => ({
        ...col,
        children: col.children
          .map((child) => ({
            child,
            score: fuzzyScoreFields(q, [
              { value: child.label, weight: 1 },
              { value: child.description ?? '', weight: 0.4 },
              { value: col.header, weight: 0.6 },
            ]),
          }))
          .filter((entry) => entry.score > 0)
          .sort((a, b) => b.score - a.score)
          .map((entry) => entry.child),
      }))
      .filter((col) => col.children.length > 0);
  }, [isPalette, visibleColumnGroups, paletteQuery]);

  // #1320 — recent rail items, capped to the panel's free slots after
  // pinned items take their reserved priority slots. With pinning not
  // yet implemented, the rail just shows the most recent
  // `RECENT_PANEL_SLOTS` items.
  const recentRailItems = useMemo(() => {
    if (!isPalette || !recentItems) return [] as NavChild[];
    return recentItems.slice(0, RECENT_PANEL_SLOTS - PINNED_SLOT_COUNT);
  }, [isPalette, recentItems]);

  // The actual columns that get rendered — filtered when palette is in
  // search mode, untouched otherwise.
  const renderColumnGroups = filteredColumnGroups ?? visibleColumnGroups;

  // #1346 — palette grid keyboard handler. ↑/↓ moves within the current
  // column, ←/→ jumps to the adjacent column at the same row (or the
  // nearest available row when columns aren't the same length). Enter
  // is intentionally left alone — the focused `<a>` activates it natively
  // — and Tab is also untouched so DOM order (column-major) cycles cards
  // exactly as the ticket spec asks. Esc closes the panel; the existing
  // pinned-panel Esc handler at the parent level handles pinned palettes
  // and we delegate to `closeDropdown` here for the non-pinned case.
  const handlePaletteKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (!isPalette) return;
      // Escape closes the panel regardless of focus location inside it.
      if (e.key === 'Escape') {
        e.preventDefault();
        closeDropdown(group.label);
        return;
      }
      if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown' && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') {
        return;
      }
      const panel = dropdownRef.current;
      if (!panel) return;
      const cards = Array.from(panel.querySelectorAll<HTMLElement>('[data-palette-card]'));
      if (cards.length === 0) return;
      const current = document.activeElement as HTMLElement | null;

      // Build a column → ordered-row map for ←/→ row matching.
      // Note: `Map` from lucide-react shadows the global Map at module
      // scope (see the `Map` import + the `globalThis.Map` workaround in
      // the Recent rail index below) — reach for the JS built-in via
      // `globalThis` so this doesn't accidentally call the React icon.
      const byColumn = new globalThis.Map<number, HTMLElement[]>();
      for (const card of cards) {
        const col = Number(card.dataset.paletteCol);
        if (!byColumn.has(col)) byColumn.set(col, []);
        byColumn.get(col)!.push(card);
      }
      const columnIndices = Array.from(byColumn.keys()).sort((a: number, b: number) => a - b);

      // If focus isn't on a grid card yet (e.g. the search input has
      // focus and the user pressed an arrow key), seed it to the first
      // card so the arrow press has a meaningful starting position.
      // ↑ from the input does nothing — the input owns its caret keys.
      const currentIsCard = current && current.hasAttribute('data-palette-card');
      if (!currentIsCard) {
        if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
          e.preventDefault();
          cards[0]?.focus();
        }
        return;
      }

      const curCol = Number(current!.dataset.paletteCol);
      const curRow = Number(current!.dataset.paletteRow);
      const sameColCards = byColumn.get(curCol) ?? [];

      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        // Within-column traversal. The DOM row order matches the
        // post-filter render order, so index in `sameColCards` is the
        // visible row. Clamp at the edges so wrapping doesn't fight
        // the user.
        const curIdx = sameColCards.findIndex((c) => c === current);
        if (curIdx < 0) return;
        const nextIdx = e.key === 'ArrowDown' ? curIdx + 1 : curIdx - 1;
        if (nextIdx < 0 || nextIdx >= sameColCards.length) return;
        sameColCards[nextIdx].focus();
        return;
      }

      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault();
        const colPos = columnIndices.indexOf(curCol);
        const nextCol = e.key === 'ArrowRight' ? columnIndices[colPos + 1] : columnIndices[colPos - 1];
        if (nextCol === undefined) return;
        const targetCards = byColumn.get(nextCol) ?? [];
        if (targetCards.length === 0) return;
        // Land on the equivalent row when present; otherwise the
        // nearest row (clamp to the column's tail when shorter).
        const targetRow = Math.min(curRow, targetCards.length - 1);
        const match = targetCards.find((c) => Number(c.dataset.paletteRow) === targetRow)
          ?? targetCards[Math.min(targetRow, targetCards.length - 1)];
        match?.focus();
      }
    },
    [isPalette, closeDropdown, group.label],
  );
  const paletteHasQuery = isPalette && !!(paletteQuery ?? '').trim();
  const paletteNoResults = paletteHasQuery && (renderColumnGroups?.length ?? 0) === 0;

  return (
    <div
      ref={triggerRef}
      className="h-full"
      data-nav-trigger
      data-nav-group={group.label}
      style={{ flexShrink: 0, height: '100%' }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      // Safety net: if the browser eats the mouseenter event because the
      // mouse approaches from below the navbar (some Chrome builds skip
      // the boundary event when the cursor crosses the 1px nav border),
      // mousemove will re-open the dropdown. setOpenGroup bails out when
      // the value is unchanged, so this is a cheap no-op while hovering.
      onMouseMove={handleMouseEnter}
    >
      {/* Top-level trigger */}
      <button
        className="flex items-center gap-1.5 h-full"
        // #1321 — click pins/unpins palette groups so the panel survives
        // hover-leave (Esc / outside-click / re-click closes it). Other
        // groups keep their hover-only behaviour — onClick is a no-op there.
        onClick={handleTriggerClick}
        aria-haspopup={group.children || group.columnGroups ? 'menu' : undefined}
        aria-expanded={isOpen}
        style={{
          padding: '0 14px',
          display: 'flex', alignItems: 'center',
          fontSize: '13px',
          fontWeight: active ? 500 : 400,
          color: active ? 'var(--vz-primary)' : 'var(--vz-heading-color)',
          backgroundColor: active ? 'var(--vz-primary-bg-subtle)' : 'transparent',
          border: 'none',
          borderBottom: active ? '2px solid var(--vz-primary)' : '2px solid transparent',
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          transition: 'color 0.15s, background-color 0.15s, border-color 0.15s',
        }}
        onMouseEnter={(e) => {
          if (!active) {
            e.currentTarget.style.color = 'var(--vz-body-color)';
            e.currentTarget.style.backgroundColor = 'var(--vz-nav-hover-bg, rgba(0,0,0,0.04))';
          }
        }}
        onMouseLeave={(e) => {
          if (!active) {
            e.currentTarget.style.color = active ? 'var(--vz-primary)' : 'var(--vz-heading-color)';
            e.currentTarget.style.backgroundColor = active ? 'var(--vz-primary-bg-subtle)' : 'transparent';
          }
        }}
      >
        <Icon style={{ width: '15px', height: '15px' }} />
        <span>{group.label}</span>
        <ChevronDown
          style={{
            width: '12px',
            height: '12px',
            marginLeft: '2px',
            opacity: 0.5,
            transition: 'transform 0.15s',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        />
      </button>

      {/* Dropdown panel — portaled to body to escape overflow clip */}
      {isOpen && dropdownPos && createPortal(
        <div
          ref={dropdownRef}
          className="mega-dropdown-enter"
          data-nav-panel
          data-nav-group={group.label}
          // #1321 — pinned palettes survive hover-leave; only Esc /
          // outside-click / re-click on the trigger closes them.
          onMouseEnter={() => { if (closeTimer.current) clearTimeout(closeTimer.current); }}
          onMouseLeave={() => { if (!pinned) closeDropdown(group.label); }}
          // #1346 — palette grid keyboard navigation (↑/↓/←/→). Enter
          // is handled natively by the focused `<a>` and Tab uses DOM
          // order, which is column-major in the rendered tree.
          onKeyDown={handlePaletteKeyDown}
          style={{
            position: 'fixed',
            top: dropdownPos.top,
            left: dropdownPos.left,
            minWidth: visibleColumnGroups ? `${visibleColumnGroups.length * 220}px` : '260px',
            backgroundColor: 'var(--vz-card-bg)',
            border: '1px solid var(--vz-border-color)',
            borderRadius: '8px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.08)',
            padding: '8px',
            zIndex: 9999,
            // ATL-000369 — the panel is fixed-positioned at the trigger's
            // bottom edge, so on a short window (small laptop / zoomed-in
            // display) the tallest column ran off the bottom with no way to
            // reach the last items. Cap the panel to the space between its
            // top edge and the viewport bottom (8px breathing room) and let
            // it scroll vertically.
            maxHeight: `calc(100vh - ${dropdownPos.top}px - 8px)`,
            overflowY: 'auto',
          }}
        >
          {/* #1319 — palette mode renders a search input at the top of the
              dropdown so the user can fuzzy-filter all settings live.
              #1320 — Recent rail originally rendered below the search input.
              #1514 — Recent rail now sits inline to the right of the search
              input on the same row (search ~70% left, rail flush right).
              Wrapper is `flex` with `flexWrap: 'wrap'` so the rail drops to
              a second row on extremely narrow panels (responsive fallback,
              not the primary state). The rail is hidden while the user is
              typing so it doesn't compete with results, and when empty the
              search input naturally claims the full row width. */}
          {isPalette && (
            <div
              data-testid="settings-palette-search-row"
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 8,
                margin: '0 4px 6px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '6px 10px',
                  // Search owns the left ~70% of the row; `flex: 1` lets it
                  // expand to fill the remainder when the rail is hidden
                  // (empty state or active search), and `minWidth: 0` lets
                  // it shrink if the rail wraps.
                  flex: '1 1 280px',
                  minWidth: 0,
                  border: '1px solid var(--vz-border-color, #e9ebec)',
                  borderRadius: 6,
                  backgroundColor: 'var(--vz-body-bg, #f8fafc)',
                }}
              >
                <Search style={{ width: 14, height: 14, color: 'var(--vz-text-muted, #878a99)', flexShrink: 0 }} />
                <input
                  ref={paletteInputRef}
                  type="text"
                  value={paletteQuery ?? ''}
                  onChange={(e) => setPaletteQuery?.(e.target.value)}
                  placeholder={t('components.sidebar.searchSettings')}
                  autoFocus
                  aria-label={t('components.sidebar.searchSettings')}
                  data-testid="settings-palette-search"
                  style={{
                    flex: 1,
                    minWidth: 0,
                    border: 'none',
                    background: 'transparent',
                    outline: 'none',
                    fontSize: 13,
                    color: 'var(--vz-heading-color)',
                  }}
                />
                <span
                  aria-hidden
                  style={{
                    fontSize: 10,
                    color: 'var(--vz-text-muted, #878a99)',
                    border: '1px solid var(--vz-border-color, #e9ebec)',
                    borderRadius: 4,
                    padding: '1px 5px',
                    background: 'var(--vz-card-bg)',
                    fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                    flexShrink: 0,
                  }}
                >
                  {'⌘K'}
                </span>
              </div>

              {/* #1320 / #1514 — Recent rail. Hidden while the user is
                  searching so it doesn't compete with results. Sits inline
                  to the right of the search input as a compact pill row:
                  clock icon → small uppercase "RECENT" label → up to 3
                  rounded-full chips. When there are no recents, the entire
                  rail is omitted and the search input claims the full row. */}
              {!paletteHasQuery && recentRailItems.length > 0 && (
                <div
                  data-testid="settings-recent-rail"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    flexShrink: 0,
                  }}
                >
                  <Clock
                    aria-hidden
                    style={{
                      width: 12,
                      height: 12,
                      color: 'var(--vz-text-muted, #878a99)',
                      flexShrink: 0,
                    }}
                  />
                  <span
                    style={{
                      fontSize: 10.5,
                      fontWeight: 600,
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      color: 'var(--vz-text-muted, #878a99)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {t('components.sidebar.recent')}
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    {recentRailItems.map((child) => (
                      <Link
                        key={`recent-${child.value}`}
                        to={child.href}
                        onClick={() => closeDropdown(group.label)}
                        data-testid={`settings-recent-chip-${child.value}`}
                        aria-label={child.description ? `${child.label} · ${child.description}` : child.label}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          padding: '4px 11px',
                          borderRadius: 9999,
                          fontSize: 12,
                          fontWeight: 500,
                          lineHeight: 1.2,
                          whiteSpace: 'nowrap',
                          textDecoration: 'none',
                          color: isChildActive(child.href)
                            ? 'var(--vz-primary)'
                            : 'var(--vz-primary)',
                          backgroundColor: 'var(--vz-primary-bg-subtle)',
                          border: '1px solid transparent',
                          transition: 'background-color 0.1s, border-color 0.1s',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor =
                            'var(--vz-primary)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = 'transparent';
                        }}
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {renderColumnGroups ? (
            paletteNoResults ? (
              <div
                data-testid="settings-palette-empty"
                style={{
                  padding: '24px 16px',
                  fontSize: 13,
                  color: 'var(--vz-text-muted, #878a99)',
                  textAlign: 'center',
                }}
              >
                {t('components.sidebar.noSettingsMatch', { query: paletteQuery })}
              </div>
            ) : (
              /* ── Multi-column mega-menu ── */
              <div style={{ display: 'flex', gap: '4px' }}>
                {renderColumnGroups.map((col, colIdx) => (
                  <div
                    key={col.header}
                    style={{
                      flex: 1,
                      minWidth: '200px',
                      paddingRight: colIdx < renderColumnGroups.length - 1 ? '4px' : 0,
                      borderRight: colIdx < renderColumnGroups.length - 1
                        ? '1px solid var(--vz-border-color, #e9ebec)'
                        : 'none',
                    }}
                  >
                    {/* Column header + #1345 tinted accent bar.
                        The bar pulls the same hex from `col.iconTint` (sourced
                        from `SETTINGS_COLUMN_TINTS`) so the swatch on the
                        icons and the stripe under the header read as the
                        same column tint — one source of truth, no hex
                        duplication. */}
                    <div style={{
                      padding: '4px 12px 4px',
                      fontSize: '10.5px',
                      fontWeight: 600,
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      color: 'var(--vz-text-muted, #878a99)',
                    }}>
                      {col.header}
                    </div>
                    {col.iconTint && (
                      <div
                        aria-hidden
                        style={{
                          height: 2,
                          margin: '0 12px 6px',
                          borderRadius: 1,
                          backgroundColor: col.iconTint,
                        }}
                      />
                    )}
                    {/* Column items */}
                    {col.children.map((child, rowIdx) => {
                      // #1322 — when the column declares an `iconTint`, that
                      // colour wins over the default `--vz-body-bg` swatch on
                      // inactive items. Active items still adopt the primary
                      // subtle so the selection cue overrides the column tint.
                      const inactiveIconBg = col.iconTint ?? 'var(--vz-body-bg, rgba(0,0,0,0.03))';
                      return (
                        <NavChildLink
                          key={child.label}
                          child={child}
                          iconTintBg={inactiveIconBg}
                          active={isChildActive(child.href)}
                          onSelect={() => closeDropdown(group.label)}
                          // #1346 — coordinates feed the arrow-key handler
                          // attached to the panel root. Tab order follows
                          // DOM order (column-major: col 1 top→bottom, then
                          // col 2 top→bottom, …) which matches the ticket
                          // spec without any explicit tabindex juggling.
                          paletteCol={isPalette ? colIdx : undefined}
                          paletteRow={isPalette ? rowIdx : undefined}
                          // #1350 — bold the card title when this row
                          // lives inside the Settings palette. Non-palette
                          // mega-menus (if any future column-group nav
                          // reuses this branch) keep the original weight.
                          palette={isPalette}
                        />
                      );
                    })}
                  </div>
                ))}
              </div>
            )
          ) : (
            /* ── Standard flat list ── */
            visibleChildren.map((child) => (
              <NavChildLink
                key={child.label}
                child={child}
                iconTintBg="var(--vz-body-bg, rgba(0,0,0,0.03))"
                active={isChildActive(child.href)}
                onSelect={() => closeDropdown(group.label)}
              />
            ))
          )}

          {/* #1505 — Palette footer (palette-mode only). Hosts the keyboard
              navigation hints (↑↓ Navigate · Enter Open · Esc Close — all
              already wired by #1346) on the left and reserves a right-aligned
              slot for the upcoming Trucky deep-link affordance. The button
              itself is a non-functional placeholder; its click handler is
              deliberately a no-op until the Trucky integration ships in a
              follow-up ticket (see WAYP-trucky-deeplink TODO below). */}
          {isPalette && !paletteNoResults && (
            <div
              data-testid="palette-footer"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                marginTop: 6,
                paddingTop: 8,
                paddingLeft: 12,
                paddingRight: 12,
                paddingBottom: 4,
                borderTop: '1px solid var(--vz-border-color, #e9ebec)',
                fontSize: 11,
                color: 'var(--vz-text-muted, #878a99)',
              }}
            >
              {/* Left: keyboard hints — descriptive only; the wiring already
                  exists in handlePaletteKeyDown (#1346) and the parent-level
                  Esc handler. Each hint pairs a glyph in a thin border with
                  its action label, matching the visual rhythm of the ⌘K
                  badge in the search row above. */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {[
                  { keys: '↑↓', label: 'Navigate' },
                  { keys: 'Enter', label: 'Open' },
                  { keys: 'Esc', label: 'Close' },
                ].map((hint) => (
                  <span
                    key={hint.label}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                  >
                    <kbd
                      style={{
                        fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                        fontSize: 10,
                        border: '1px solid var(--vz-border-color, #e9ebec)',
                        borderRadius: 4,
                        padding: '1px 5px',
                        background: 'var(--vz-card-bg)',
                        color: 'var(--vz-text-muted, #878a99)',
                      }}
                    >
                      {hint.keys}
                    </kbd>
                    <span>{hint.label}</span>
                  </span>
                ))}
              </div>

              {/* Right: Trucky Hook scaffold. Non-functional placeholder —
                  the future Trucky deep-link integration will wire onClick
                  to open a Trucky-AI conversation seeded with the current
                  Settings context and accept a deep-link reply that
                  navigates the palette to the requested setting. Visible
                  label doubles as aria-label per WCAG 2.1 SC 2.5.3
                  (visible-label-matches-accessible-name). Click intentionally
                  does NOT close the palette so a future inline Trucky panel
                  can compose with the open palette state. */}
              <button
                type="button"
                data-testid="palette-footer-trucky-hook"
                aria-label="Ask Trucky to deep-link any setting"
                onClick={(e) => {
                  // TODO(WAYP-trucky-deeplink): open the Trucky AI deep-link
                  // panel seeded with the current palette query / focused
                  // settings card. Until then, no-op — must NOT close the
                  // palette (stopPropagation prevents the panel's
                  // outside-click handler from firing on bubble) and must
                  // NOT navigate. The reserved slot keeps the footer width
                  // stable so the keyboard hints don't reflow when the real
                  // affordance lands.
                  e.stopPropagation();
                }}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  background: 'transparent',
                  border: 'none',
                  padding: '2px 4px',
                  borderRadius: 4,
                  cursor: 'pointer',
                  color: 'var(--vz-primary)',
                  fontSize: 11,
                  fontFamily: 'inherit',
                  lineHeight: 1.2,
                  outline: 'none',
                }}
                onFocus={(e) => {
                  // Reuse the same focus-ring idiom the rest of the file
                  // relies on (the palette cards use the native browser
                  // outline on focus). Using a 2px box-shadow ring keeps
                  // the visual consistent with the existing palette focus
                  // affordances in jsdom + real browsers.
                  e.currentTarget.style.boxShadow = '0 0 0 2px var(--vz-primary-bg-subtle)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <Sparkles aria-hidden style={{ width: 12, height: 12 }} />
                <span>{t('components.sidebar.askTrucky')}</span>
              </button>
            </div>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Exports                                                            */
/* ------------------------------------------------------------------ */

export { HorizontalNav as Sidebar };

export function HorizontalNav() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  // #1573 — hover-intent timer for `navigateOnHover` nav groups (Reports).
  const hoverNavTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mobileNavOpen = useUIStore((s) => s.mobileNavOpen);
  const toggleMobileNav = useUIStore((s) => s.toggleMobileNav);
  const setMobileNavOpen = useUIStore((s) => s.setMobileNavOpen);
  // Build hidden-nav-key set from enterprise features. While features are loading,
  // returns an empty set (show everything) to avoid a flash of hidden nav.
  const { data: features } = useEnterpriseFeatures();
  const equipmentExpiriesEnabled  = useFeature('advanced_equipment_expiries');
  const fileChecklistEnabled      = useFeature('enable_file_checklist');
  const driverExpiriesEnabled     = useFeature('driver_expiries');
  const enableFiltersEnabled      = useFeature('enable_filters');
  const workOrdersEnabled         = useFeature('workorders');
  const purchaseOrdersEnabled     = useFeature('purchase_orders');
  // WAYP-501: Slips — two-layer gate (mod_usps_main enabled AND enable_slip preference true)
  const slipsModuleEnabled        = useFeature('mod_usps_main');
  const slipsPreferenceEnabled    = useFeaturePreference<boolean>('mod_usps_main', 'enable_slip', false);
  // ATL-000726: Process EDI needs the EDI feature *in addition to* mod_usps_main
  // (which it now shares with the rest of the USPS Carrier group). `useFeature`
  // is fail-closed on a missing/loading feature list, unlike the
  // NAV_VALUE_TO_FEATURE_KEY sweep below.
  const ediFeatureEnabled         = useFeature('edi');
  // #1790 — Commodity Categories master nav: gated by the `commodity_category`
  // toggle inside the Dispatch (General Freight) feature's configJson.
  const commodityCategoryEnabled  = useFeaturePreference<boolean>('mod_dispatch_freight_main', 'commodity_category', false);
  // Frequency Types / USPS Trips — layer-2 preference gate against a now-
  // retired `hide_contracts_and_trips` toggle. Ticket #1091 (v3 feedback
  // wave) dropped the toggle from the Feature Management form, but legacy
  // orgs with `hide_contracts_and_trips: true` already stored in
  // `OrganizationFeature.configJson` keep this gate active until the
  // server-side visibility checks (`frequency-types.visibility.ts`) are
  // retired in a separate sweep.
  const hideContractsAndTrips     = useFeaturePreference<boolean>('mod_usps_main', 'hide_contracts_and_trips', false);
  // Recurring Loads layer-3 gate: per-org opt-in preference stored at
  // `mod_usps_main.configJson.recurring_loads` (V1-mirrored toggle; the
  // legacy V3 `enableRecurringLoads` duplicate was retired in ticket #1091).
  // Default OFF. Nav entry hides when this is false even though both
  // `mod_usps_main` (layer 1) and `recurring:view` (layer 2) may pass.
  const recurringLoadsEnabled     = useFeaturePreference<boolean>('mod_usps_main', 'recurring_loads', false);
  // WAYP-395: Customs Brokers — second-layer gate. `mod_customs_broker`
  // licence is the first layer (NAV_VALUE_TO_FEATURE_KEY); this preference
  // is the operational toggle the org admin flips in Company Preferences →
  // Brokerage. Both must be true for the nav entry to render.
  const enableCustomBrokerInfoNav = useFeaturePreference<boolean>('mod_ftl_brokerage_main', 'enable_custom_broker_info', false);
  // QA-3 (#1403): Location Types is the master-data table that backs
  // the Quadrant taxonomy used by Location Zones. The page is only
  // meaningful when the `location_zones.use_quadrants` preference is
  // ON — otherwise an org has no surface that consumes the type rows.
  // Default false so the entry stays hidden for orgs that have not
  // opted in.
  const locationQuadrantsEnabled  = useFeaturePreference<boolean>('location_zones', 'use_quadrants', false);
  // DAT – ATL-001428 (B1): the `dat_loadboard` `search` preference (default ON).
  const datSearchEnabled          = useFeaturePreference<boolean>('dat_loadboard', 'search', true);
  // DAT Margin Calculator: the `margin_calculator` preference (default ON).
  const datMarginEnabled          = useFeaturePreference<boolean>('dat_loadboard', 'margin_calculator', true);
  // DAT: the org's Type of Business must offer load search (a Shipper posts only).
  const datRoleAccess             = useDatRoleAccess();
  // WAYP-504: Taxes — gated by CompanyInfo.enableTaxes (company-info field, not enterprise feature).
  // Fail-safe hidden while loading or when the response is null/false.
  const { data: companyInfo, isLoading: companyInfoLoading } = useCompanyInfo();
  // WAYP-814: `enableQuotes` lives on `/settings/company-preferences` (added in
  // WAYP-804), not on `/settings/company-info`. Same react-query cache pattern
  // as the Taxes gate — fail-safe hidden while loading.
  const { data: companyPreferences, isLoading: companyPreferencesLoading } = useCompanyPreferences();
  // FourKites Tracking nav gate — the Operations dashboard appears only when the
  // org has at least one FourKites-enabled EDI trading partner. Fail-safe hidden
  // while the query is loading (undefined → length 0).
  const { data: fourkitesPartners } = useFourkitesPartners();
  const fourkitesNavEnabled = (fourkitesPartners?.length ?? 0) > 0;
  const terminalLabel = useFeaturePreference('enable_filters', 'terminal_filter_name', 'Terminal');
  // WAYP-927 — Driver Routes (BID) module name is admin-customisable via
  // `mod_usps_main → bids_module_name`. Sidebar label switches in lock-step
  // with the list / detail page titles.
  const driverRouteModuleName = useDriverRouteModuleName();
  // WAYP-506 — the Fleet > Drivers & Carriers row label is dynamic per
  // Organization.businessType. Broker → "Carriers"; Mixed → "Drivers &
  // Carriers"; everything else (Carrier / Shipper / Other / null / loading) →
  // "Drivers". Applied to the in-dropdown child labeled (historically)
  // "Drivers & Carriers" AND to the mobile equivalent via the same remap.
  const fleetLabels = useFleetLabels();
  const hiddenNavKeys = useMemo(() => {
    const hidden = new Set<string>();
    if (!features) return hidden;
    const enabledByKey: Record<string, boolean> = {};
    for (const f of features) enabledByKey[f.key] = f.isEnabled;
    for (const [navValue, featureKey] of Object.entries(NAV_VALUE_TO_FEATURE_KEY)) {
      if (featureKey && enabledByKey[featureKey] === false) {
        hidden.add(navValue);
      }
    }
    // #1790 — hide the Commodity Categories master unless the Dispatch
    // (General Freight) `commodity_category` preference toggle is on.
    if (!commodityCategoryEnabled) hidden.add('commodity-categories');
    if (!equipmentExpiriesEnabled) hidden.add('equipment-expiry-types');
    if (!driverExpiriesEnabled) hidden.add('driver-expiry-types');
    if (!enableFiltersEnabled) hidden.add('terminals');
    if (!fileChecklistEnabled) hidden.add('file-checklist');
    if (!datSearchEnabled || datRoleAccess?.searchLoads === false) hidden.add('dat-find-loads');
    if (datMarginEnabled === false || datRoleAccess?.priceLoads === false) hidden.add('dat-margin-calculator');
    // WAYP-368: Work Order Types — gated by workorders enterprise feature
    if (!workOrdersEnabled) hidden.add('work-order-types');
    // WAYP-369: Purchase Order Types — gated by purchase_orders enterprise feature
    if (!purchaseOrdersEnabled) hidden.add('purchase-order-types');
    // WAYP-501: Slips — hidden unless mod_usps_main enabled AND enable_slip preference true
    if (!slipsModuleEnabled || !slipsPreferenceEnabled) hidden.add('slips');
    // ATL-000726: Process EDI is layer-1 gated on `mod_usps_main` (above) and
    // additionally requires the `edi` feature — an org can run the USPS module
    // without EDI. Keeping the EDI requirement here rather than in
    // NAV_VALUE_TO_FEATURE_KEY lets the entry demand BOTH features; that map
    // holds one key per nav value.
    if (!ediFeatureEnabled) hidden.add('usps-process-edi');
    // USPS 5500 (#1556) — layer-3 preference gate. Hidden unless the
    // "What Slip Reason is used to record the 5500" preference
    // (`organization.usps500`) is set. Fail-safe hidden while the company-
    // preferences query is loading.
    const usps500Pref = companyPreferences?.usps500;
    const usps5500Configured = Array.isArray(usps500Pref)
      ? usps500Pref.length > 0
      : usps500Pref != null && usps500Pref !== '';
    if (companyPreferencesLoading || !usps5500Configured) hidden.add('usps-5500');
    // #1770 — CLEAR Rates (#1652), Voucher Import (#1763) and USPS Reconciliation
    // are reconciliation surfaces gated on the SLIP workflow being enabled, not
    // the `usps500` 5500-slip-reason preference (per Jorge 2026-06-23). Same
    // two-layer gate as Slips: USPS Carrier feature (mod_usps_main) + the
    // `enable_slip` company preference.
    if (!slipsModuleEnabled || !slipsPreferenceEnabled) {
      hidden.add('clear-rates');
      hidden.add('vouchers');
      hidden.add('usps-reconciliation');
      hidden.add('usps-no-miles-reconciliation');
    }
    // Reconcile Slips (#1564) — no longer gated on the `usps500` slip-reason
    // preference (the module shows ALL USPS-variant load slips now). Visibility
    // is the `mod_usps_main` feature (NAV_VALUE_TO_FEATURE_KEY) +
    // `reconcile_slips:view` (NAV_VALUE_TO_PERMISSION) only.
    // Frequency Types / USPS Trips — layer-2 preference gate (legacy
    // `hide_contracts_and_trips` flag; see hook comment above).
    if (hideContractsAndTrips) hidden.add('frequency-types');
    if (hideContractsAndTrips) hidden.add('usps-trips');
    // Recurring Loads layer-3 visibility gate. Hidden when the
    // `recurring_loads` preference is OFF, regardless of whether
    // `mod_usps_main` (layer 1) and `recurring:view` (layer 2) would pass.
    if (!recurringLoadsEnabled) hidden.add('usps-recurring-loads');
    // WAYP-504: Taxes — gated by CompanyInfo.enableTaxes. Fail-safe hidden
    // while the company-info query is loading or returned null, to prevent
    // a flash of visible Taxes before the response resolves (AC-13).
    if (companyInfoLoading || !companyInfo?.enableTaxes) hidden.add('taxes');
    // WAYP-395: Customs Brokers — hidden when the operational toggle
    // (`enable_custom_broker_info`) is OFF even if the licence is ON.
    // Layer 1 (mod_customs_broker disabled) is handled by the
    // NAV_VALUE_TO_FEATURE_KEY loop above, so this branch only handles the
    // preference flip.
    if (!enableCustomBrokerInfoNav) hidden.add('customs-brokers');
    // QA-3 (#1403): Location Types hidden unless the `use_quadrants`
    // preference is ON. Fail-safe hides while the feature payload is
    // loading (handled by the early-return at the top of the memo).
    if (!locationQuadrantsEnabled) hidden.add('location-types');
    // WAYP-814: per-module Quote menu gating. Each module's Quotes
    // menu entry consults the matching per-module "Enable Quote
    // Workflow" toggle on Company Preferences:
    //   Dispatch       → companyPreferences.enableQuotes
    //   USPS Carrier   → companyPreferences.enableQuotesUspsCarrier
    //   Intermodal     → companyPreferences.enableQuotesIntermodal
    //   FTL Brokerage  → companyPreferences.enableQuotesFtlbrokerage
    //   Livestock      → companyPreferences.enableQuotesLivestock
    // Same fail-safe pattern as Taxes: hide while loading or when the
    // preference is null/false. The enterprise-feature gate
    // (`mod_*_main`) still applies on top — both must pass for the
    // entry to render.
    if (companyPreferencesLoading || !companyPreferences?.enableQuotes) hidden.add('quotes');
    // ATL-000802 — the Dispatch Calendar placeholder row is deleted, so the
    // WAYP-945 `hidden.add` that kept it out of the nav goes with it. (Dispatch
    // History un-hidden in WAYP-1246; Tender un-hidden when the Dispatch Tenders
    // surface shipped — EDI / API / EBA / History tabs.)
    // ATL-000802 — the per-variant Calendar placeholders (WAYP-945 pivot
    // 2026-05-11) are removed with their `hidden.add` guards: none of
    // `/intermodal/calendar`, `/livestock/calendar` or `/freight/calendar` was
    // ever routed. (Per-variant History entries stay — un-hidden in WAYP-1246,
    // they route to LoadsHistoryPage with `?variant=` carrying the slug.)
    if (companyPreferencesLoading || !companyPreferences?.enableQuotesUspsCarrier) hidden.add('usps-quotes');
    if (companyPreferencesLoading || !companyPreferences?.enableQuotesIntermodal) hidden.add('intermodal-quotes');
    if (companyPreferencesLoading || !companyPreferences?.enableQuotesFtlbrokerage) hidden.add('ftl-brokerage-quotes');
    if (companyPreferencesLoading || !companyPreferences?.enableQuotesLivestock) hidden.add('livestock-quotes');
    // WAYP-952 — Rating sub-page gating driven by the per-org
    // customer:rating preferences:
    //   rate_type: 'Rate Types' | 'Lanes/Locations' | 'Lanes/Cities'
    //     → exactly ONE of billing-rates / lane-location-rates /
    //       lane-city-rates is visible.
    //   enable_fsc_per_customer / enable_accessorial_rates toggle
    //     their respective sub-items.
    const ratingFeature = features.find((f) => f.key === 'customer:rating');
    const ratingPrefs = (ratingFeature?.configJson ?? {}) as Record<string, unknown>;
    for (const key of ratingHiddenNavKeys(ratingPrefs)) hidden.add(key);
    // Weight-Based Zone Rating — the Zones master nav entry is hidden unless
    // the `weight_based_zone_rating` enterprise feature is enabled. (`zones`
    // isn't keyed in NAV_VALUE_TO_FEATURE_KEY by feature key — that map is
    // nav→permission — so gate it explicitly here against `enabledByKey`.)
    if (enabledByKey['weight_based_zone_rating'] !== true) hidden.add('zones');
    // FourKites Tracking — data-driven gate: hidden unless the org has ≥1
    // FourKites-enabled EDI trading partner (fail-safe hidden while loading).
    if (!fourkitesNavEnabled) hidden.add('fourkites-monitoring');
    return hidden;
  }, [features, equipmentExpiriesEnabled, driverExpiriesEnabled, fileChecklistEnabled, enableFiltersEnabled, workOrdersEnabled, purchaseOrdersEnabled, slipsModuleEnabled, slipsPreferenceEnabled, ediFeatureEnabled, commodityCategoryEnabled, hideContractsAndTrips, recurringLoadsEnabled, enableCustomBrokerInfoNav, locationQuadrantsEnabled, companyInfo, companyInfoLoading, companyPreferences, companyPreferencesLoading, fourkitesNavEnabled, datSearchEnabled, datMarginEnabled, datRoleAccess]);
  const canView = usePermissionsStore((s) => s.canView);
  const permissionsLoaded = usePermissionsStore((s) => s.loaded);
  const user = useAuthStore((s) => s.user);
  const isSuperAdmin = user?.role === 'super_admin';
  // ATL-001415 — Email Intake settings live in Integrations, so the people who
  // configure Email Intake reach Integrations too (it then shows only the
  // integrations they manage; the rest stays super-admin only).
  const emailIntakeOn = useFeature('ai_email_load_creation');
  const canConfigureEmailIntake = usePermissionsStore((s) => s.can('email_intake', 'configure'));
  const integrationsForIntakeAdmin =
    emailIntakeOn && canConfigureEmailIntake && canView('integrations');
  // Org-level dashboard ACL overrides — falls back to hardcoded defaults until loaded.
  const { data: dashboardPerms } = useDashboardPermissions();
  const dashboardGrants = dashboardPerms
    ? { dashboards: dashboardPerms.matrix.dashboards, widgets: dashboardPerms.matrix.widgets }
    : undefined;
  const allowedDashboardIds = useMemo(
    () => new Set(resolveAllowedDashboardsForRole(user?.role, dashboardGrants)),
    [user?.role, dashboardGrants],
  );

  /* ---- Nav groups ---- */
  // Enterprise Features + Integrations are rendered under Settings > System Settings
  // (gated via NavChild.superAdminOnly).

  // Apply dynamic labels (e.g., Terminal → custom name from enterprise features;
  // Fleet > Drivers & Carriers → businessType-driven label per WAYP-506). Both
  // transforms happen in the same pass so we only rebuild the nav tree once
  // per render.
  const dashboardOrder = useDashboardStore((s) => s.order);
  const dashboardMap = useDashboardStore((s) => s.dashboards);
  const customDashboards = useMemo(
    () => dashboardOrder.map((id) => dashboardMap[id]).filter((d) => d && !d.builtIn),
    [dashboardOrder, dashboardMap],
  );
  // Server-side org-shared dashboards. Filtered by the role's ACL grants
  // server-side, so anything that comes back here is already permitted.
  const { data: sharedDashboards } = useSharedDashboards();
  const allNavGroups: NavGroup[] = useMemo(() => {
    const needsTerminalRemap = !!terminalLabel && terminalLabel !== 'Terminal';
    const fleetNavLabel = fleetLabels.navLabel;
    // Default Fleet child label is "Drivers & Carriers" (the Mixed variant).
    // Skip the whole rebuild when no transform is needed.
    const needsFleetRemap = fleetNavLabel !== 'Drivers & Carriers';
    // WAYP-927 — Driver Routes module name remap. Lives under the USPS Carrier
    // group, which uses a flat `children` array (not `columnGroups`).
    const needsDriverRouteRemap = driverRouteModuleName.plural !== 'Driver Routes';
    // Custom + shared dashboards — appended as extra children under the
    // Dashboard nav group.
    const hasCustom = customDashboards.length > 0;
    const hasShared = (sharedDashboards?.length ?? 0) > 0;

    // First pass: inject dashboard extras into the Dashboard group so the
    // subsequent label remaps operate on the enriched tree.
    const base = buildNavGroups(t).map((g) => {
      if (g.key !== 'dashboard' || !g.children || (!hasCustom && !hasShared)) return g;
      const extras: NavChild[] = [
        ...(sharedDashboards ?? []).map((d) => ({
          label: d.name,
          value: 'dashboard',
          href: `/dashboard/shared/${d.id}`,
          icon: Users,
          description: 'Shared with organization',
        })),
        ...customDashboards.map((d) => ({
          label: d.name,
          value: 'dashboard',
          href: d.href,
          icon: LayoutDashboard,
          description: 'Personal dashboard',
        })),
      ];
      return { ...g, children: [...g.children, ...extras] };
    });

    if (!needsTerminalRemap && !needsFleetRemap && !needsDriverRouteRemap) return base;

    // Helper — remap a flat children list for the cases that target it
    // (Fleet "drivers" label, USPS "driver-routes" label).
    const remapChildren = (group: NavGroup, children: NavChild[]): NavChild[] => {
      let out = children;
      if (group.key === 'fleet' && needsFleetRemap) {
        out = out.map((c) => (c.value === 'drivers' ? { ...c, label: fleetNavLabel } : c));
      }
      if (needsDriverRouteRemap) {
        out = out.map((c) =>
          c.value === 'driver-routes'
            ? {
                ...c,
                label: driverRouteModuleName.plural,
                description: `Recurring driver/truck schedules — ${driverRouteModuleName.plural}`,
              }
            : c,
        );
      }
      return out;
    };

    return base.map((g) => {
      // Path A — flat children array (Fleet, USPS Carrier).
      if (g.children) {
        const next = remapChildren(g, g.children);
        if (next !== g.children) g = { ...g, children: next };
      }

      // Path B — column-grouped (Settings). Terminals lives here. Driver
      // Routes does NOT, but the loop also runs the children-style remap
      // through inner column children defensively in case the nav config
      // is ever restructured.
      if (g.columnGroups && (needsTerminalRemap || needsDriverRouteRemap)) {
        g = {
          ...g,
          columnGroups: g.columnGroups.map((col) => {
            let cs = col.children;
            if (needsTerminalRemap) {
              cs = cs.map((c) =>
                c.value === 'terminals'
                  ? { ...c, label: terminalLabel, description: `Manage ${String(terminalLabel).toLowerCase()} locations` }
                  : c,
              );
            }
            if (needsDriverRouteRemap) {
              cs = cs.map((c) =>
                c.value === 'driver-routes'
                  ? {
                      ...c,
                      label: driverRouteModuleName.plural,
                      description: `Recurring driver/truck schedules — ${driverRouteModuleName.plural}`,
                    }
                  : c,
              );
            }
            return { ...col, children: cs };
          }),
        };
      }

      return g;
    });
  }, [t, terminalLabel, fleetLabels.navLabel, driverRouteModuleName.plural, customDashboards, sharedDashboards]);

  /* ---- desktop dropdown state ----
   * Default is hover-peek; palette groups (Settings) can additionally be
   * pinned (#1321) so they survive hover-leave until Esc / outside-click /
   * re-click on the trigger. `pinnedGroup` tracks the currently-pinned
   * group label (only one at a time, mirrors `openGroup`). */
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const [pinnedGroup, setPinnedGroup] = useState<string | null>(null);

  const openDropdown = useCallback((label: string, opts?: { pinned?: boolean }) => {
    setOpenGroup(label);
    if (opts?.pinned) setPinnedGroup(label);
  }, []);

  const closeDropdown = useCallback((label?: string) => {
    setOpenGroup((prev) => (label === undefined || prev === label ? null : prev));
    setPinnedGroup((prev) => (label === undefined || prev === label ? null : prev));
  }, []);

  // #1319 — palette search query (per-open lifecycle — cleared when the
  // dropdown closes so a re-open starts fresh).
  const [paletteQuery, setPaletteQuery] = useState('');
  const paletteInputRef = useRef<HTMLInputElement | null>(null);

  // #1319 — global Cmd+K (Ctrl+K on Windows) opens the Settings palette
  // from anywhere in the app and focuses the search input.
  // #1321 — opening via Cmd+K also pins the panel so the user can mouse
  // away to the result they want without dismissing the palette.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const isMod = e.metaKey || e.ctrlKey;
      if (!isMod || e.key.toLowerCase() !== 'k') return;
      e.preventDefault();
      setOpenGroup('Settings');
      setPinnedGroup('Settings');
      setPaletteQuery('');
      // Wait a tick for the input to mount before focusing it.
      setTimeout(() => paletteInputRef.current?.focus(), 0);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // #1321 — outside-click + Esc dismissal for pinned panels. Without
  // these the pinned Settings palette would survive a click anywhere on
  // the page, which is hostile UX.
  useEffect(() => {
    if (!pinnedGroup) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpenGroup(null);
        setPinnedGroup(null);
      }
    };
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      // The dropdown panel is portaled outside the nav, so we identify
      // both surfaces by data-attributes attached on render.
      if (target.closest('[data-nav-trigger]') || target.closest('[data-nav-panel]')) {
        return;
      }
      setOpenGroup(null);
      setPinnedGroup(null);
    };
    document.addEventListener('keydown', handleKey);
    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.removeEventListener('mousedown', handleClick);
    };
  }, [pinnedGroup]);

  // Clear the palette search when the dropdown closes so the next open
  // starts at a blank query.
  useEffect(() => {
    if (!openGroup) setPaletteQuery('');
  }, [openGroup]);

  /* ---- mobile accordion state ---- */
  const [expandedMobileGroup, setExpandedMobileGroup] = useState<string | null>(null);

  /* ---- scroll arrows (kept as fallback) ---- */
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkOverflow = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const tolerance = 2;
    setCanScrollLeft(el.scrollLeft > tolerance);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - tolerance);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    checkOverflow();
    el.addEventListener('scroll', checkOverflow, { passive: true });
    const ro = new ResizeObserver(checkOverflow);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', checkOverflow);
      ro.disconnect();
    };
  }, [checkOverflow]);

  const scroll = useCallback((direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const amount = direction === 'left' ? -200 : 200;
    el.scrollBy({ left: amount, behavior: 'smooth' });
  }, []);

  /* Close dropdown when route changes */
  useEffect(() => {
    setOpenGroup(null);
    setMobileNavOpen(false);
  }, [location.pathname, setMobileNavOpen]);

  /* ---- helpers ---- */

  /** Filter children by hiddenNavKeys, user permissions, and super-admin gating */
  const getVisibleChildren = (children: NavChild[]): NavChild[] =>
    children.filter((c) => {
      if (hiddenNavKeys.has(c.value)) return false;
      const dashboardId = NAV_VALUE_TO_DASHBOARD_ID[c.value];
      if (dashboardId && !allowedDashboardIds.has(dashboardId)) return false;
      if (!passesSuperAdminGate(c, isSuperAdmin, integrationsForIntakeAdmin)) return false;
      // If permissions haven't loaded yet, show everything (avoid flash of hidden nav)
      if (!permissionsLoaded) return true;
      const permModule = NAV_VALUE_TO_PERMISSION[c.value];
      if (permModule && !canView(permModule)) return false;
      return true;
    });

  /** Filter columnGroups — removes groups whose children are all hidden */
  const getVisibleColumnGroups = (cols: NavColumnGroup[]): NavColumnGroup[] =>
    cols
      .map((col) => ({ ...col, children: getVisibleChildren(col.children) }))
      .filter((col) => col.children.length > 0);

  /** Build visible groups: hide groups where all children are hidden or no view permission */
  const visibleGroups = allNavGroups.filter((g) => {
    if (g.href && g.value) {
      if (hiddenNavKeys.has(g.value)) return false;
      if (permissionsLoaded) {
        const permModule = NAV_VALUE_TO_PERMISSION[g.value];
        if (permModule && !canView(permModule)) return false;
      }
      return true;
    }
    if (g.columnGroups) return getVisibleColumnGroups(g.columnGroups).length > 0;
    if (g.children) return getVisibleChildren(g.children).length > 0;
    return true;
  });

  /**
   * Check if a group is active. A direct-link group matches on its own href;
   * a parent group matches if any visible child's href matches the current
   * path. Normally the match is `pathname === href` OR `pathname.startsWith(
   * href + '/')`. Setting `exact: true` on a group or child strips the
   * `startsWith` half, so a legacy entry whose href is a prefix of another
   * group's routes does not falsely highlight.
   */
  const matchHref = (href: string, exact?: boolean): boolean => {
    if (exact) return location.pathname === href;
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  const isGroupActive = (group: NavGroup): boolean => {
    if (group.href) {
      return matchHref(group.href, group.exact);
    }
    if (group.columnGroups) {
      return group.columnGroups.some((col) =>
        getVisibleChildren(col.children).some((child) => matchHref(child.href, child.exact))
      );
    }
    if (group.children) {
      return getVisibleChildren(group.children).some((child) => matchHref(child.href, child.exact));
    }
    return false;
  };

  const isChildActive = (href: string): boolean =>
    location.pathname === href;

  /* ---- #1320 — Recent Settings rail ----
   * Pull the recent-href list from the per-user server pref and resolve
   * each href to a live NavChild from the Settings group so labels /
   * icons stay in sync with whatever the nav config says today. */
  const { data: recentHrefs } = useSettingsRecents();
  const trackRecent = useTrackSettingsRecent();
  const settingsGroup = useMemo(
    () => allNavGroups.find((g) => g.key === 'settings'),
    [allNavGroups],
  );
  const settingsHrefIndex = useMemo(() => {
    // Note: lucide-react exports a `Map` icon component that shadows the
    // global Map constructor at module scope. Reach for the global via
    // globalThis to avoid the icon name binding.
    const map = new globalThis.Map<string, NavChild>();
    if (!settingsGroup?.columnGroups) return map;
    for (const col of settingsGroup.columnGroups) {
      for (const child of col.children) {
        map.set(child.href, child);
      }
    }
    return map;
  }, [settingsGroup]);
  const recentChildren = useMemo(() => {
    if (!recentHrefs || recentHrefs.length === 0) return [] as NavChild[];
    const seen = new Set<string>();
    const out: NavChild[] = [];
    for (const href of recentHrefs) {
      if (seen.has(href)) continue;
      const child = settingsHrefIndex.get(href);
      if (child) {
        out.push(child);
        seen.add(href);
      }
    }
    return out;
  }, [recentHrefs, settingsHrefIndex]);

  // Track navigation to /settings/* routes so the Recent rail reflects
  // the actual click history. Only fires when the URL is one we
  // recognise from the Settings group config — the trackRecent
  // callback de-dupes against its own last-tracked href, so this is
  // safe to call on every render.
  useEffect(() => {
    const child = settingsHrefIndex.get(location.pathname);
    if (!child) return;
    trackRecent(location.pathname);
  }, [location.pathname, settingsHrefIndex, trackRecent]);

  return (
    <>
      {/* Scrollbar-hide + dropdown animation styles */}
      <style>{`
        .nav-scroll-hide::-webkit-scrollbar { display: none; }
        .nav-scroll-hide { -ms-overflow-style: none; scrollbar-width: none; }
        .mega-dropdown-enter {
          animation: megaDropIn 0.15s ease-out;
        }
        @keyframes megaDropIn {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <nav
        className="fixed left-0 right-0 z-[1001]"
        style={{
          top: '50px',
          height: '46px',
          backgroundColor: 'var(--vz-card-bg)',
          borderBottom: '1px solid var(--vz-border-color)',
          transition: 'background-color 0.2s, border-color 0.2s',
        }}
      >
        <div className="h-full flex items-center" style={{ padding: '0 16px' }}>
          {/* Mobile hamburger */}
          <button
            className="lg:hidden flex items-center justify-center mr-2"
            style={{
              width: '34px',
              height: '34px',
              color: 'var(--vz-heading-color)',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              borderRadius: '4px',
            }}
            onClick={toggleMobileNav}
          >
            {mobileNavOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>

          {/* Desktop nav — scrollable container */}
          <div className="hidden lg:flex items-center h-full relative" style={{ flex: '1 1 0%', minWidth: 0 }}>
            {/* Left scroll arrow */}
            {canScrollLeft && (
              <button
                onClick={() => scroll('left')}
                aria-label="Scroll nav left"
                style={{
                  position: 'absolute',
                  left: 0, top: 0, bottom: 0, zIndex: 2,
                  width: '32px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: 'none', cursor: 'pointer',
                  background: 'linear-gradient(to right, var(--vz-card-bg) 60%, transparent)',
                  color: 'var(--vz-heading-color)',
                }}
              >
                <ChevronLeft style={{ width: '16px', height: '16px' }} />
              </button>
            )}

            {/* Right scroll arrow */}
            {canScrollRight && (
              <button
                onClick={() => scroll('right')}
                aria-label="Scroll nav right"
                style={{
                  position: 'absolute',
                  right: 0, top: 0, bottom: 0, zIndex: 2,
                  width: '32px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: 'none', cursor: 'pointer',
                  background: 'linear-gradient(to left, var(--vz-card-bg) 60%, transparent)',
                  color: 'var(--vz-heading-color)',
                }}
              >
                <ChevronRight style={{ width: '16px', height: '16px' }} />
              </button>
            )}

            {/* Fade gradients */}
            {canScrollLeft && (
              <div aria-hidden style={{
                position: 'absolute', left: '32px', top: 0, bottom: 0, width: '16px',
                background: 'linear-gradient(to right, var(--vz-card-bg), transparent)',
                zIndex: 1, pointerEvents: 'none',
              }} />
            )}
            {canScrollRight && (
              <div aria-hidden style={{
                position: 'absolute', right: '32px', top: 0, bottom: 0, width: '16px',
                background: 'linear-gradient(to left, var(--vz-card-bg), transparent)',
                zIndex: 1, pointerEvents: 'none',
              }} />
            )}

            {/* Scrollable nav list */}
            <div
              ref={scrollRef}
              className="nav-scroll-hide flex items-center gap-0.5 h-full"
              style={{ overflowX: 'auto', flex: '1 1 0%' }}
            >
              {visibleGroups.map((group) => {
                const Icon = group.icon;
                const active = isGroupActive(group);
                const hasChildren = group.children && getVisibleChildren(group.children).length > 0;
                const isOpen = openGroup === group.label;

                /* Direct link — no dropdown */
                if (group.href && !hasChildren) {
                  return (
                    <Link
                      key={group.label}
                      to={group.href}
                      className="flex items-center gap-1.5"
                      style={{
                        padding: '0 14px',
                        height: '100%',
                        display: 'flex', alignItems: 'center',
                        fontSize: '13px',
                        fontWeight: active ? 500 : 400,
                        color: active ? 'var(--vz-primary)' : 'var(--vz-heading-color)',
                        backgroundColor: active ? 'var(--vz-primary-bg-subtle)' : 'transparent',
                        textDecoration: 'none',
                        borderBottom: active ? '2px solid var(--vz-primary)' : '2px solid transparent',
                        transition: 'color 0.15s, background-color 0.15s, border-color 0.15s',
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                      }}
                      onMouseEnter={(e) => {
                        if (!active) {
                          e.currentTarget.style.color = 'var(--vz-body-color)';
                          e.currentTarget.style.backgroundColor = 'var(--vz-nav-hover-bg, rgba(0,0,0,0.04))';
                        }
                        // #1737 — hover-intent navigation. Lingering on
                        // the nav button for 100ms triggers navigate.
                        // 250ms felt unresponsive ("doesn't work") so
                        // tightened to 100ms — fast enough to feel
                        // instant on a deliberate hover, still long
                        // enough that cursors just passing by don't
                        // fire the timer.
                        if (group.navigateOnHover && group.href && !active) {
                          if (hoverNavTimer.current) clearTimeout(hoverNavTimer.current);
                          const href = group.href;
                          hoverNavTimer.current = setTimeout(() => navigate(href), 100);
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!active) {
                          e.currentTarget.style.color = 'var(--vz-heading-color)';
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }
                        if (hoverNavTimer.current) {
                          clearTimeout(hoverNavTimer.current);
                          hoverNavTimer.current = null;
                        }
                      }}
                    >
                      <Icon style={{ width: '15px', height: '15px' }} />
                      <span>{group.label}</span>
                    </Link>
                  );
                }

                /* Group with dropdown */
                const visibleChildren = getVisibleChildren(group.children || []);
                const visibleColumnGroups = group.columnGroups
                  ? getVisibleColumnGroups(group.columnGroups)
                  : undefined;
                const isPaletteGroup = !!group.palette;
                return (
                  <NavGroupDropdown
                    key={group.label}
                    group={group}
                    Icon={Icon}
                    active={active}
                    isOpen={isOpen}
                    visibleChildren={visibleChildren}
                    visibleColumnGroups={visibleColumnGroups}
                    openDropdown={openDropdown}
                    closeDropdown={closeDropdown}
                    isChildActive={isChildActive}
                    pinned={isPaletteGroup && pinnedGroup === group.label}
                    paletteInputRef={isPaletteGroup ? paletteInputRef : undefined}
                    paletteQuery={isPaletteGroup ? paletteQuery : undefined}
                    setPaletteQuery={isPaletteGroup ? setPaletteQuery : undefined}
                    recentItems={isPaletteGroup ? recentChildren : undefined}
                  />
                );
              })}
            </div>
          </div>
        </div>

        {/* Mobile dropdown — expandable groups */}
        {mobileNavOpen && (
          <div
            className="lg:hidden absolute left-0 right-0 overflow-y-auto"
            style={{
              top: '46px',
              maxHeight: 'calc(100vh - 96px)',
              backgroundColor: 'var(--vz-card-bg)',
              borderTop: '1px solid var(--vz-border-color)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            }}
          >
            {visibleGroups.map((group) => {
              const Icon = group.icon;
              const active = isGroupActive(group);
              const hasChildren = group.children && getVisibleChildren(group.children).length > 0;
              const isExpanded = expandedMobileGroup === group.label;

              /* Direct link — no children */
              if (group.href && !hasChildren) {
                return (
                  <Link
                    key={group.label}
                    to={group.href}
                    onClick={() => setMobileNavOpen(false)}
                    className="flex items-center gap-3"
                    style={{
                      padding: '10px 20px',
                      fontSize: '13.5px',
                      color: active ? 'var(--vz-primary)' : 'var(--vz-heading-color)',
                      backgroundColor: active ? 'var(--vz-primary-bg-subtle)' : 'transparent',
                      textDecoration: 'none',
                      borderLeft: active ? '3px solid var(--vz-primary)' : '3px solid transparent',
                    }}
                  >
                    <Icon style={{ width: '16px', height: '16px' }} />
                    <span>{group.label}</span>
                  </Link>
                );
              }

              /* Group with expandable children */
              const mobileColumnGroups = group.columnGroups
                ? getVisibleColumnGroups(group.columnGroups)
                : undefined;
              const visibleChildren = mobileColumnGroups
                ? mobileColumnGroups.flatMap((col) => [
                    { label: col.header, value: '__header__', href: '', icon: Settings, sectionHeader: true } as NavChild,
                    ...col.children,
                  ])
                : getVisibleChildren(group.children || []);
              return (
                <div key={group.label}>
                  {/* Group header */}
                  <button
                    onClick={() =>
                      setExpandedMobileGroup(isExpanded ? null : group.label)
                    }
                    className="flex items-center gap-3 w-full"
                    style={{
                      padding: '10px 20px',
                      fontSize: '13.5px',
                      color: active ? 'var(--vz-primary)' : 'var(--vz-heading-color)',
                      backgroundColor: active ? 'var(--vz-primary-bg-subtle)' : 'transparent',
                      textDecoration: 'none',
                      cursor: 'pointer',
                      textAlign: 'left',
                      border: 'none',
                      borderLeft: active ? '3px solid var(--vz-primary)' : '3px solid transparent',
                    }}
                  >
                    <Icon style={{ width: '16px', height: '16px' }} />
                    <span style={{ flex: 1 }}>{group.label}</span>
                    <ChevronDown
                      style={{
                        width: '14px',
                        height: '14px',
                        opacity: 0.4,
                        transition: 'transform 0.2s',
                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                      }}
                    />
                  </button>

                  {/* Expanded children */}
                  {isExpanded && (
                    <div style={{ backgroundColor: 'var(--vz-body-bg, rgba(0,0,0,0.02))' }}>
                      {visibleChildren.map((child) => {
                        if (child.sectionHeader) {
                          return (
                            <div
                              key={child.label}
                              style={{
                                padding: '10px 20px 3px 44px',
                                fontSize: '10.5px',
                                fontWeight: 600,
                                letterSpacing: '0.06em',
                                textTransform: 'uppercase',
                                color: 'var(--vz-text-muted, #878a99)',
                              }}
                            >
                              {child.label}
                            </div>
                          );
                        }
                        const ChildIcon = child.icon;
                        const childActive = isChildActive(child.href);
                        return (
                          <Link
                            key={child.label}
                            to={child.href}
                            onClick={() => setMobileNavOpen(false)}
                            className="flex items-center gap-3"
                            style={{
                              padding: '9px 20px 9px 44px',
                              fontSize: '13px',
                              color: childActive ? 'var(--vz-primary)' : 'var(--vz-heading-color)',
                              backgroundColor: childActive ? 'var(--vz-primary-bg-subtle)' : 'transparent',
                              textDecoration: 'none',
                            }}
                          >
                            <ChildIcon style={{ width: '14px', height: '14px', opacity: 0.7 }} />
                            <div>
                              <div>{child.label}</div>
                              {child.description && (
                                <div style={{
                                  fontSize: '11px',
                                  color: 'var(--vz-text-muted, #878a99)',
                                  marginTop: '1px',
                                }}>
                                  {child.description}
                                </div>
                              )}
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </nav>
    </>
  );
}
